/**
 * The plan grid — Plan Lines by month, hours in the cells (RP-4.1, RP-4.4).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * TWO ENTRY MODES, AND WHY
 * ──────────────────────────────────────────────────────────────────────────────
 * **MonthByMonth** is the spreadsheet: rows are Plan Lines, columns are months, cells are
 * hours, with keyboard navigation, fill, paste and undo. That is what a Total-cadence
 * envelope needs, because its hours genuinely differ month to month.
 *
 * **SteadyState** (RP-4.4) is for a Monthly plan, which is a RUN-RATE. Typing the same
 * number into 12 or 36 identical columns is data entry the shape of the plan should have
 * spared you, so a steady-state plan has ONE editable "Hrs / mo" figure per line and the
 * month columns become a read-only projection — hidden by default, one toggle away when
 * you want to see the demand curve. The projection is derived server-side, so the totals,
 * margin and FTE row are computed from the expansion and the two modes are
 * indistinguishable downstream.
 *
 * The selection, fill, paste and undo machinery is therefore MonthByMonth-only: in
 * steady-state there is nothing to select.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WORKSTREAMS ARE OPTIONAL
 * ──────────────────────────────────────────────────────────────────────────────
 * A plan is usable with no Workstreams at all. Lines live in the default "General" group
 * (the API always emits it, empty or not) and Workstreams are opt-in categorization on
 * top — so nobody has to invent one before typing an hour.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * PERFORMANCE — WHY THIS IS NOT SHAPED LIKE RateCardManager
 * ──────────────────────────────────────────────────────────────────────────────
 * `src/components/admin/RateCardManager.tsx` is the state precedent (flat composite-key
 * draft, `inputMode="decimal"` with string values, `""` meaning absent, regex-gated
 * writes, one batched save, the sticky-left recipe). Two of its choices do not survive
 * 1,440 cells and are deliberately designed out:
 *
 *   - It clones the whole draft per keystroke. Here the draft is a `Map` behind a ref:
 *     O(1) write, never cloned.
 *   - Its dirty check is a double `JSON.stringify` per render. Here it is a `Set` of
 *     dirty keys, so "is anything dirty" is `.size > 0`.
 *
 * A keystroke bumps ONE row's version, so one `<GridRow>` re-renders — not 1,440.
 * Measured at the RP-4.1 bar (40 × 36): ~5ms per keystroke, versus ~4ms on a 30-cell
 * plan, so the cost is flat in grid size. Totals derive in a `useMemo` keyed on
 * `useDeferredValue(gridVersion)`.
 *
 * On virtualization: the house rule (quoted in `LogsView.tsx`) is "Lists virtualize past
 * 50 rows. Paginate at 100", with pagination as the established workaround rather than a
 * new dependency. RP-4.1's bar is 40 Plan Lines, under it. Revisit past ~60.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * GEOMETRY
 * ──────────────────────────────────────────────────────────────────────────────
 * The frozen block is built by `buildGeo` rather than hard-coded constants, because
 * steady-state inserts an extra column and every sticky `left` offset is a cumulative sum
 * of the widths before it. One flag moves all of them, and the colSpans, together.
 */

import { useBlocker } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  Plus,
  Ruler,
  UserLock,
  Users,
  X,
} from "lucide-react";
import {
  createContext,
  Fragment,
  memo,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { InternalRateChip } from "@/components/plans/InternalRateChip";
import { describeRateSource } from "@/components/plans/rate-source";
import { fitExplanation } from "@/components/plans/SuggestedResourcePicker";
import { FitBadge } from "@/components/shared/FitBadge";
import { ReservationBadge } from "@/components/shared/ReservationBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLaborCategories } from "@/hooks/use-rate-card";
import { usePlanGuidanceLoad, usePlanLineFits } from "@/hooks/use-resource-plans";
import { monthRevenueBasis, spreadByWeights } from "@/lib/fee-spread";
import type { GuidanceTier } from "@/lib/guidance-tier";
import {
  type CellPatch,
  type CellRef,
  cellKey,
  clampRef,
  isSingleCell,
  isTypableHours,
  parseCellValue,
  parseTsv,
  planFill,
  planHandleDrag,
  planPaste,
  rectContains,
  type Selection,
  selectionRect,
  UndoStack,
} from "@/lib/plan-grid/grid-state";
import { loadTooltip, planHoursByResourceMonth, projectedLoad } from "@/lib/plan-guidance-load";
import {
  DEFAULT_SHARE_POINTS,
  type PlanLineShareInput,
  sharesAfterRemoving,
  sharesFromDraft,
} from "@/lib/plan-line-share-edits";
import {
  formatShareHolders,
  isPrimaryPosition,
  PRIMARY_SPLIT_REFUSAL,
} from "@/lib/plan-line-shares";
import {
  isSplitLine,
  PLAN_LINE_STATUSES,
  type PlanLineStatus,
  planLineStatus,
} from "@/lib/plan-line-status";
import { canEditPlanContent } from "@/lib/plan-version";
import { parseRampMap, rampHoursCenti } from "@/lib/resource-plan-math";
import { describeSuggestedResourceReason } from "@/lib/suggested-resource-reason";
import { cn } from "@/lib/utils";
import type {
  PlanLineShareView,
  PlanLineView,
  ResourcePickerFitView,
  ResourcePickerReservationView,
  ResourcePlanDetailView,
  WorkstreamView,
} from "@/schemas/resource-plans";

/** One month's load bar for a row's suggested Resource — tier + the hover maths. */
interface MonthLoad {
  tier: GuidanceTier;
  tooltip: string;
}

/** One Resource's NOS guidance for one month, as the guidance-load overlay reads it. */
interface MonthGuidance {
  guidanceHours: number | null;
  capacityHours: number | null;
  /** Hours reserved for ANOTHER Account this month — counted as load. null = unknown. */
  reservedHours: number | null;
}

export const W_MONTH = 62;

/**
 * The editable "Hrs / mo" figure (RP-4.4). It is the ONE thing a steady-state plan asks
 * you to type, so it has to read as a field at rest — a bordered box with a "0"
 * placeholder — not as blank space that only reveals itself on focus. The month-by-month
 * cells stay borderless because the grid lines already make them a spreadsheet.
 */
const RUN_RATE_INPUT =
  "h-7 w-full rounded-md border border-input bg-background px-1.5 text-right text-sm tabular-nums shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground hover:border-primary/60 focus:border-ring focus:ring-[3px] focus:ring-ring/50";

/**
 * Height of each footer row below the Total row (Revenue per month, FTE per month). The
 * footer rows are all sticky to the bottom, so each needs its OWN bottom offset — with a
 * shared `bottom: 0` they pile onto one another and only the last one painted is legible.
 */
export const FOOT_SUB_ROW_H = 26;

export interface Slot {
  left: number;
  width: number;
}

interface Geo {
  category: Slot;
  who: Slot;
  /** Rate and Amount — left out of the staffing view. */
  rate: Slot | null;
  /** Only in SteadyState — the editable run-rate (RP-4.4). */
  runRate: Slot | null;
  hours: Slot;
  amount: Slot | null;
  /** Cost and Margin — only for a reader holding `viewPlanCost`, and only when shown. */
  cost: Slot | null;
  margin: Slot | null;
  frozenWidth: number;
  /** How many `<td>`s the frozen block spans, for every colSpan in the table. */
  frozenCols: number;
}

/**
 * Cumulative left offsets from the widths, so the two can never drift apart.
 *
 * 27 months × 62px is ~1,674px on top of a ~614px frozen block, so a 1440px laptop sees
 * about 13 of them: the freeze is not optional. Two-axis freeze is greenfield in this
 * repo — the corner cells carry the highest z so they win over both the sticky month
 * header and the sticky first columns.
 */
function buildGeo(steadyState: boolean, showMoney: boolean, showPricing: boolean): Geo {
  let cursor = 0;
  const col = (width: number): Slot => {
    const left = cursor;
    cursor += width;
    return { left, width };
  };
  const category = col(248);
  // Wide enough for the avatar, the Resource's full name and their Fit Score chip —
  // planners read WHO is on a line, and initials alone made them hover every row.
  const who = col(196);
  const rate = showPricing ? col(116) : null;
  const runRate = steadyState ? col(78) : null;
  const hours = col(68);
  const amount = showPricing ? col(92) : null;
  const cost = showMoney ? col(92) : null;
  const margin = showMoney ? col(76) : null;
  return {
    category,
    who,
    rate,
    runRate,
    hours,
    amount,
    cost,
    margin,
    frozenWidth: cursor,
    frozenCols: 3 + (steadyState ? 1 : 0) + (showPricing ? 2 : 0) + (showMoney ? 2 : 0),
  };
}

/** `2026-01` → `Jan 26`, for a 62px column. */
export function monthLabel(month: string): string {
  const [year, m] = month.split("-");
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${names[Number(m) - 1] ?? m} ${year?.slice(2) ?? ""}`;
}

/** Thousands separators for an hours/amount figure that arrives as a decimal string. */
export function fmtNum(value: string): string {
  const [whole = "0", frac] = value.split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // Hours read better without a trailing ".00"; amounts keep their cents.
  return frac === undefined || frac === "00" ? withCommas : `${withCommas}.${frac}`;
}

/**
 * A money figure, grid-formatted. Wraps `fmtNum` rather than `formatPlanMoney`
 * (src/lib/plan-format.ts) because this grid's convention drops a trailing ".00" —
 * `formatPlanMoney` always keeps cents, which would add ".00" across the grid.
 */
export function fmtMoney(value: string): string {
  return `$${fmtNum(value)}`;
}

/** A money figure rounded to the dollar — for a 62px month column, where cents do not fit. */
export function fmtWholeMoney(value: string): string {
  const [whole = "0", frac = "0"] = value.split(".");
  const negative = whole.startsWith("-");
  const digits = negative ? whole.slice(1) : whole;
  const rounded = BigInt(digits) + (Number(`0.${frac}`) >= 0.5 ? 1n : 0n);
  return `${negative ? "-" : ""}$${fmtNum(rounded.toString())}`;
}

interface GridRowModel {
  line: PlanLineView;
  /**
   * Index into the flat, visible row order — the grid's y coordinate. -1 for a Share row
   * that is not part of the selection (a reader who cannot edit Shares, or SteadyState).
   */
  rowIndex: number;
  /**
   * The draft key this row's cells live under: the line id, or `lineId~resourceId` for an
   * RP-24 Line Share row. Selection, fill, paste and undo all address rows by this key, so
   * a Share row behaves exactly like a line row.
   */
  rowKey: string;
  /** Present on a Line Share row (RP-24). */
  share?: PlanLineShareView;
}

/** The draft key of a Line Share row (RP-24). `~` never appears in a line id. */
function shareRowKey(lineId: string, resourceId: string): string {
  return `${lineId}~${resourceId}`;
}

function isShareRowKey(rowKey: string): boolean {
  return rowKey.includes("~");
}

function lineIdOfRowKey(rowKey: string): string {
  return rowKey.split("~")[0] ?? rowKey;
}

interface GroupModel {
  workstream: WorkstreamView;
  rows: GridRowModel[];
}

export interface PlanGridProps {
  plan: ResourcePlanDetailView;
  /**
   * The Staffing Requests page's view: who is on each line and their hours, nothing else.
   * The plan's content — line hours, rates, adding lines and Workstreams — is locked while
   * the Who column, status chip and Line Shares stay live, and every money figure (Rate,
   * Amount, Cost, Margin and the revenue / margin / cost rows) is left out.
   */
  staffingView?: boolean;
  /** MonthByMonth: every dirty cell. */
  onSaveCells: (cells: Array<{ planLineId: string; month: string; hours: string | null }>) => void;
  /** SteadyState: one hours-per-month figure per changed line (RP-4.4). */
  onSaveRunRates: (runRates: Array<{ planLineId: string; hours: string | null }>) => void;
  saving: boolean;
  onAddLine: (input: {
    workstreamId: string | null;
    laborCategoryId: string;
    clientLaborCategory: string | null;
    billRate?: string;
    rateOverrideReason?: string;
    steadyStateHours?: string | null;
  }) => void;
  addingLine: boolean;
  onAddWorkstream: () => void;
  onOpenLine: (line: PlanLineView) => void;
  onOpenWorkstream: (workstream: WorkstreamView) => void;
  /** The Who column's click target — opens the Suggested Resource picker (RP-18.2). */
  onOpenWho: (line: PlanLineView, anchor: HTMLElement) => void;
  /**
   * RP-24 — save Line Share sets. Called after `onSaveCells` / `onSaveRunRates` in the same
   * save, so the host can send them after the line hours they are checked against.
   */
  onSaveShares?: (
    sets: Array<{ planLineId: string; shares: PlanLineShareInput[]; message?: string }>,
  ) => Promise<boolean> | undefined;
  /** RP-24 — "+ Add Resource" in a line's subtree: the picker, in Share mode. */
  onAddShareResource?: (line: PlanLineView, anchor: HTMLElement) => void;
  onConfirmWho: (line: PlanLineView) => void;
  /**
   * The Labor Category's status chip — Open / Proposed / Confirmed. `anchor` is the chip,
   * so a status that needs a Resource first can open the picker beside it.
   */
  onSetLineStatus: (line: PlanLineView, status: PlanLineStatus, anchor: HTMLElement) => void;
  /** The Rate cell's click target on an editable plan — opens the inline rate editor. */
  onEditRate: (line: PlanLineView, anchor: HTMLElement) => void;
  /**
   * RP-4.3. Optional so the grid still renders without it — the Solution drawer's
   * read-only preview passes no ramp handler and gets no ramp row.
   */
  onSetRamp?: (months: Array<{ month: string; factor: string }>) => void;
  /** Opens the sizing / mix dialog for a Workstream (RP-3.2, RP-3.3). */
  onSizeWorkstream?: (workstream: WorkstreamView) => void;
  /**
   * The eye icon — opens the suggested Resource's profile drawer over the
   * plan. Optional: the row only offers it once the guidance-load overlay confirms
   * that Resource is viewable, and the grid still renders fine without a handler.
   */
  onViewResource?: (resourceId: string) => void;
  /** RP-32.2 — the line's Candidates (the RM's shortlist), anchored to the Who cell. */
  onOpenCandidates?: (line: PlanLineView, anchor: HTMLElement) => void;
  /** RP-32.4 — the line's notes thread. */
  onOpenNotes?: (line: PlanLineView) => void;
}

/**
 * RP-4.3's ramp row.
 *
 * One editable factor per month, 0–1. A month at 1.000 shows MUTED, because the default
 * is "fully mobilised" and 36 columns each shouting "1.000" would bury the two the planner
 * actually de-rated. Committed on blur and on Enter rather than per keystroke: a factor
 * re-values every total on the plan, so a round trip per digit would make the summary
 * strip flicker through nonsense.
 */
/**
 * Plan months NOS has closed (`YYYY-MM`) on a plan linked to a NOS Demand. Their hours
 * came from NOS and the server refuses any change to them, so every cell in those columns
 * — a line's, a Share's, the ramp's — renders read-only. A context rather than a prop:
 * the cells sit five memoised row components down.
 */
const LockedMonthsContext = createContext<ReadonlySet<string>>(new Set());

const CLOSED_MONTH_TITLE = "Closed in NOS — these hours come from NOS and can't be changed here.";

function RampRow({
  geo,
  months,
  byMonth,
  canEdit,
  onSetRamp,
  top,
}: {
  geo: Geo;
  months: readonly string[];
  byMonth: ResourcePlanDetailView["byMonth"];
  canEdit: boolean;
  onSetRamp: (months: Array<{ month: string; factor: string }>) => void;
  /** The month header row's measured height — this row pins directly under it. */
  top: number;
}) {
  const factorFor = (month: string): string =>
    byMonth.find((m) => m.month === month)?.rampFactor ?? "1.000";
  const locked = useContext(LockedMonthsContext);

  return (
    <tr>
      <th
        colSpan={geo.frozenCols}
        /*
         * `top` is the header row's MEASURED height, floored: the Start / End markers make
         * that row taller whenever the plan's first or last month is on screen, so a fixed
         * offset would let this row slide over the month labels. Flooring overlaps by under
         * a pixel rather than leaving a sliver of scrolling body visible between the two.
         */
        /*
         * OPAQUE, not `bg-muted/50`. A sticky row with a translucent background lets the
         * body scroll visibly THROUGH it — the ramp factors and the first Plan Line's
         * cells render on top of each other. The header row above uses opaque `bg-muted`
         * for the same reason; the ramp row is part of that pinned block and matches it.
         */
        className="sticky left-0 z-30 border-b border-border bg-muted px-2 py-1 text-left text-xs font-medium text-muted-foreground"
        style={{ width: geo.frozenWidth, minWidth: geo.frozenWidth, top }}
        title="Effective hours = entered hours × ramp. A partial first month is one edit, not thirty."
      >
        Ramp
      </th>
      {months.map((month) => {
        const factor = factorFor(month);
        const full = factor === "1.000";
        return (
          <th
            key={month}
            className="sticky z-20 border-b border-l border-border bg-muted p-0"
            style={{ width: W_MONTH, minWidth: W_MONTH, top }}
          >
            <input
              type="text"
              inputMode="decimal"
              defaultValue={factor}
              disabled={!canEdit || locked.has(month)}
              title={locked.has(month) ? CLOSED_MONTH_TITLE : undefined}
              aria-label={`Ramp factor for ${month}`}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  e.currentTarget.value = factor;
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                const next = e.currentTarget.value.trim();
                if (next === factor) return;
                // An empty cell means "no ramp", which is 1.0 — and the API stores that
                // by deleting the row.
                const value = next === "" ? "1.000" : next;
                if (!/^\d(\.\d{1,3})?$/.test(value) || Number(value) > 1) {
                  e.currentTarget.value = factor;
                  return;
                }
                onSetRamp([{ month, factor: value }]);
              }}
              className={cn(
                "h-6 w-full bg-transparent px-1.5 text-right text-xs tabular-nums outline-none",
                "focus:bg-background focus:ring-1 focus:ring-ring",
                full ? "text-muted-foreground/50" : "font-semibold text-foreground",
              )}
            />
          </th>
        );
      })}
    </tr>
  );
}

export function PlanGrid(props: PlanGridProps) {
  const { plan } = props;
  // Every part of the plan — lines, rates, hours, Workstreams — is editable in every status
  // by whoever may edit it at all; outside Draft each change lands in Plan History.
  const staffingView = props.staffingView === true;
  const canEditContent = canEditPlanContent(plan) && !staffingView;
  const readOnly = !canEditContent;
  const ratesReadOnly = readOnly;
  const hoursReadOnly = readOnly;
  // The Who column and status chip outlive Draft: a Suggested Resource can change at any
  // plan status (see setSuggestedResource). Falls back to `canEdit` for a payload that
  // predates the flag.
  const resourcesReadOnly = !(plan.canEditResources ?? plan.canEdit);
  const steadyState = plan.entryMode === "SteadyState";
  const lockedMonthList = plan.nosDemand?.lockedMonths;
  const lockedMonths = useMemo(() => new Set(lockedMonthList ?? []), [lockedMonthList]);
  // Cost and Margin per line: the server sends the figures only to ADMIN / RM / FIN
  // (`viewPlanCost`), and the reader can hide the two columns to give the months room.
  const [moneyPref, setMoneyPref] = useState(readShowMoneyPref);
  const showMoney = plan.canViewCost && moneyPref && !staffingView;
  const [summaryPref, setSummaryPref] = useState(readShowSummaryPref);
  const geo = useMemo(
    () => buildGeo(steadyState, showMoney, !staffingView),
    [steadyState, showMoney, staffingView],
  );

  /*
   * In steady-state the months are a PROJECTION, not an input — hidden by default because
   * a run-rate planner does not need to look at 12 identical columns, and one toggle away
   * when they want to see the demand curve.
   */
  const [showProjection, setShowProjection] = useState(false);
  const visibleMonths = useMemo(
    () => (steadyState && !showProjection ? [] : plan.months),
    [steadyState, showProjection, plan.months],
  );

  // ── RP-24 Line Shares: which lines are expanded, and who may edit their Shares ──
  const canManageShares = plan.canManageShares === true;
  const maxShares = plan.maxPlanLineShares ?? 3;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = useCallback((lineId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  // ── The visible row order ──
  // Line rows, grouped by Workstream. An expanded split line's Share rows follow it in the
  // flat order (so arrows, fill and paste move through them) when this reader may edit
  // them; otherwise they render as read-only rows outside the selection.
  const layout = useMemo(() => {
    const byWorkstream = new Map<string | null, PlanLineView[]>();
    for (const line of plan.lines) {
      const list = byWorkstream.get(line.workstreamId) ?? [];
      list.push(line);
      byWorkstream.set(line.workstreamId, list);
    }
    let rowIndex = 0;
    const out: GroupModel[] = [];
    const flat: GridRowModel[] = [];
    const shareRows = new Map<string, GridRowModel[]>();
    const selectableShares = canManageShares && !steadyState;
    for (const workstream of plan.workstreams) {
      const rows: GridRowModel[] = [];
      for (const line of byWorkstream.get(workstream.id) ?? []) {
        const row: GridRowModel = { line, rowIndex: rowIndex++, rowKey: line.id };
        rows.push(row);
        flat.push(row);
        if (expanded.has(line.id) && isSplitLine(line)) {
          const children = (line.shares ?? []).map((share) => ({
            line,
            share,
            rowKey: shareRowKey(line.id, share.resourceId),
            rowIndex: selectableShares ? rowIndex++ : -1,
          }));
          if (selectableShares) flat.push(...children);
          shareRows.set(line.id, children);
        }
      }
      out.push({ workstream, rows });
    }
    return { groups: out, flatRows: flat, shareRowsByLine: shareRows };
  }, [plan.lines, plan.workstreams, expanded, canManageShares, steadyState]);
  const { groups, flatRows, shareRowsByLine } = layout;
  const rowCount = flatRows.length;
  const colCount = visibleMonths.length;

  // ── The drafts ──
  // Cells for MonthByMonth, run-rates for SteadyState. Both behind refs: O(1) write,
  // never cloned. Dirty tracking is a Set, so "is anything dirty" is `.size > 0`.
  const serverCells = useMemo(() => {
    const map = new Map<string, string>();
    if (steadyState) return map;
    for (const line of plan.lines) {
      for (const cell of line.months) map.set(cellKey(line.id, cell.month), cell.hours);
      // RP-24: each Share's hours live in the same draft, under its own row key.
      for (const share of line.shares ?? []) {
        const key = shareRowKey(line.id, share.resourceId);
        for (const cell of share.months) map.set(cellKey(key, cell.month), cell.hours);
      }
    }
    return map;
  }, [plan.lines, steadyState]);

  const serverRunRates = useMemo(() => {
    const map = new Map<string, string>();
    for (const line of plan.lines) {
      map.set(line.id, line.steadyStateHours ?? "");
      for (const share of line.shares ?? []) {
        map.set(shareRowKey(line.id, share.resourceId), share.steadyStateHours ?? "");
      }
    }
    return map;
  }, [plan.lines]);

  // RP-24: a Share's RM-entered points, keyed by its row key. Present only for a reader who
  // receives points at all (ADMIN / RM); everyone else never sees the field.
  const serverPoints = useMemo(() => {
    const map = new Map<string, string>();
    for (const line of plan.lines) {
      for (const share of line.shares ?? []) {
        if (share.points !== undefined) {
          map.set(shareRowKey(line.id, share.resourceId), String(share.points));
        }
      }
    }
    return map;
  }, [plan.lines]);

  const draft = useRef<Map<string, string>>(new Map(serverCells));
  const runRateDraft = useRef<Map<string, string>>(new Map(serverRunRates));
  const dirtyKeys = useRef<Set<string>>(new Set());
  const dirtyRunRates = useRef<Set<string>>(new Set());
  const pointsDraft = useRef<Map<string, string>>(new Map(serverPoints));
  const dirtyPoints = useRef<Set<string>>(new Set());
  const undo = useRef(new UndoStack());

  const [rowVersion, setRowVersion] = useState<Record<string, number>>({});
  const [gridVersion, setGridVersion] = useState(0);
  const [dirtyCount, setDirtyCount] = useState(0);

  // Re-seed on a server change. A dirty draft is preserved — losing a screenful of typing
  // because a background refetch landed would be indefensible.
  const seededCells = useRef(serverCells);
  const seededRunRates = useRef(serverRunRates);
  const seededPoints = useRef(serverPoints);
  const reseeds = useRef(0);
  useEffect(() => {
    if (
      seededCells.current === serverCells &&
      seededRunRates.current === serverRunRates &&
      seededPoints.current === serverPoints
    ) {
      return;
    }
    seededCells.current = serverCells;
    seededRunRates.current = serverRunRates;
    seededPoints.current = serverPoints;
    if (
      dirtyKeys.current.size > 0 ||
      dirtyRunRates.current.size > 0 ||
      dirtyPoints.current.size > 0
    ) {
      return;
    }
    draft.current = new Map(serverCells);
    runRateDraft.current = new Map(serverRunRates);
    pointsDraft.current = new Map(serverPoints);
    undo.current.clear();
    setGridVersion((v) => v + 1);
    // Stamp EVERY row (line and Share) with a fresh version, never just reset to {}. A row
    // is memoized on its version, and a server-side rewrite that keeps a line's identity
    // (RP-28's Fit, which keeps every total) would otherwise leave its inputs showing the
    // old hours. Stamps count down, so they never equal a version a keystroke produced.
    reseeds.current += 1;
    const stamp = -reseeds.current;
    setRowVersion(Object.fromEntries([...serverRunRates.keys()].map((key) => [key, stamp])));
  }, [serverCells, serverRunRates, serverPoints]);

  const readCell = useCallback(
    (lineId: string, month: string): string => draft.current.get(cellKey(lineId, month)) ?? "",
    [],
  );
  const readRunRate = useCallback(
    (lineId: string): string => runRateDraft.current.get(lineId) ?? "",
    [],
  );

  const commit = useCallback((touchedLineIds: Iterable<string>) => {
    setRowVersion((prev) => {
      const next = { ...prev };
      for (const id of touchedLineIds) next[id] = (next[id] ?? 0) + 1;
      return next;
    });
    setGridVersion((v) => v + 1);
    setDirtyCount(dirtyKeys.current.size + dirtyRunRates.current.size + dirtyPoints.current.size);
  }, []);

  const readPoints = useCallback(
    (rowKey: string): string => pointsDraft.current.get(rowKey) ?? "",
    [],
  );
  const writePoints = useCallback(
    (rowKey: string, value: string) => {
      if (value === "") pointsDraft.current.delete(rowKey);
      else pointsDraft.current.set(rowKey, value);
      if (value === (serverPoints.get(rowKey) ?? "")) dirtyPoints.current.delete(rowKey);
      else dirtyPoints.current.add(rowKey);
      commit([rowKey]);
    },
    [serverPoints, commit],
  );

  /** The single cell write path. Returns the inverse patch so a caller can build an undo. */
  const writeCell = useCallback(
    (lineId: string, month: string, value: string): CellPatch => {
      const key = cellKey(lineId, month);
      const previous = draft.current.get(key) ?? "";
      // A month NOS has closed takes no typing, paste, fill or undo.
      if (lockedMonths.has(month)) return { key, previous };
      if (value === "") draft.current.delete(key);
      else draft.current.set(key, value);
      // Dirty is a diff against the SERVER value, not the previous keystroke, so typing a
      // value and then typing it back marks the cell clean again.
      if (value === (serverCells.get(key) ?? "")) dirtyKeys.current.delete(key);
      else dirtyKeys.current.add(key);
      return { key, previous };
    },
    [serverCells, lockedMonths],
  );

  const writeRunRate = useCallback(
    (lineId: string, value: string) => {
      if (value === "") runRateDraft.current.delete(lineId);
      else runRateDraft.current.set(lineId, value);
      if (value === (serverRunRates.get(lineId) ?? "")) dirtyRunRates.current.delete(lineId);
      else dirtyRunRates.current.add(lineId);
      commit([lineId]);
    },
    [serverRunRates, commit],
  );

  // ── Selection (MonthByMonth only) ──
  const [selection, setSelection] = useState<Selection>({
    anchor: { row: 0, col: 0 },
    extent: { row: 0, col: 0 },
  });
  const rect = useMemo(() => selectionRect(selection), [selection]);
  const scroller = useRef<HTMLDivElement | null>(null);
  // The month header's height, for the Ramp row pinned beneath it (see RampRow).
  const headRow = useRef<HTMLTableRowElement | null>(null);
  const [headRowH, setHeadRowH] = useState(31);
  useLayoutEffect(() => {
    const row = headRow.current;
    if (row === null) return;
    const measure = () => setHeadRowH(Math.floor(row.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const focusCell = useCallback((ref: CellRef) => {
    const el = scroller.current?.querySelector<HTMLInputElement>(
      `[data-cell="${ref.row}-${ref.col}"]`,
    );
    el?.focus();
    el?.select();
  }, []);

  const moveTo = useCallback(
    (ref: CellRef, extend: boolean) => {
      const clamped = clampRef(ref, rowCount, colCount);
      setSelection((prev) =>
        extend ? { anchor: prev.anchor, extent: clamped } : { anchor: clamped, extent: clamped },
      );
      focusCell(clamped);
    },
    [rowCount, colCount, focusCell],
  );

  const applyTargets = useCallback(
    (targets: Array<{ from: CellRef; to: CellRef }>) => {
      if (targets.length === 0) return;
      const patches: CellPatch[] = [];
      const touched = new Set<string>();
      for (const { from, to } of targets) {
        const src = flatRows[from.row];
        const dst = flatRows[to.row];
        const srcMonth = visibleMonths[from.col];
        const dstMonth = visibleMonths[to.col];
        if (!src || !dst || srcMonth === undefined || dstMonth === undefined) continue;
        patches.push(writeCell(dst.rowKey, dstMonth, readCell(src.rowKey, srcMonth)));
        touched.add(dst.rowKey);
      }
      undo.current.push(patches);
      commit(touched);
    },
    [flatRows, visibleMonths, readCell, writeCell, commit],
  );

  const doFill = useCallback(
    (direction: "down" | "right") => {
      if (hoursReadOnly || steadyState) return;
      if (isSingleCell(selection)) {
        toast.info(
          direction === "down"
            ? "Select a range first, or drag the corner handle to fill down."
            : "Select a range first, or drag the corner handle to fill across.",
        );
        return;
      }
      applyTargets(planFill(selection, direction));
    },
    [hoursReadOnly, steadyState, selection, applyTargets],
  );

  const doPaste = useCallback(
    (text: string) => {
      if (hoursReadOnly || steadyState) return;
      const block = parseTsv(text);
      if (block.length === 0) return;

      const { targets, dropped } = planPaste({
        block,
        anchor: { row: rect.top, col: rect.left },
        rowCount,
        colCount,
      });

      // Parse everything BEFORE writing anything: a paste that fails halfway would leave
      // the grid in a state the planner did not ask for and cannot describe.
      const parsed: Array<{ lineId: string; month: string; hours: string | null }> = [];
      for (const target of targets) {
        const row = flatRows[target.row];
        const month = visibleMonths[target.col];
        if (!row || month === undefined) continue;
        const result = parseCellValue(
          target.raw,
          `${monthLabel(month)} · ${row.share?.resourceName ?? row.line.laborCategoryName}`,
        );
        if (!result.ok) {
          toast.error(result.error);
          return;
        }
        parsed.push({ lineId: row.rowKey, month, hours: result.hours });
      }

      const patches: CellPatch[] = [];
      const touched = new Set<string>();
      for (const cell of parsed) {
        patches.push(writeCell(cell.lineId, cell.month, cell.hours ?? ""));
        touched.add(cell.lineId);
      }
      undo.current.push(patches);
      commit(touched);

      if (dropped > 0) {
        toast.warning(
          `Pasted ${parsed.length} cell${parsed.length === 1 ? "" : "s"}; ${dropped} fell outside the plan's months.`,
        );
      }
    },
    [
      hoursReadOnly,
      steadyState,
      rect,
      rowCount,
      colCount,
      flatRows,
      visibleMonths,
      writeCell,
      commit,
    ],
  );

  const doUndo = useCallback(() => {
    if (hoursReadOnly || steadyState) return;
    const patches = undo.current.pop();
    if (!patches) return;
    const touched = new Set<string>();
    // Reverse order, so a batch that wrote the same cell twice unwinds correctly.
    for (const patch of [...patches].reverse()) {
      const [lineId, month] = splitKey(patch.key);
      if (lineId === null || month === null) continue;
      writeCell(lineId, month, patch.previous);
      touched.add(lineId);
    }
    commit(touched);
  }, [hoursReadOnly, steadyState, writeCell, commit]);

  const onCellKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, ref: CellRef) => {
      const meta = event.metaKey || event.ctrlKey;
      const shift = event.shiftKey;

      if (meta && event.key.toLowerCase() === "z") {
        event.preventDefault();
        doUndo();
        return;
      }
      if (meta && event.key.toLowerCase() === "d") {
        event.preventDefault();
        doFill("down");
        return;
      }
      if (meta && event.key.toLowerCase() === "r") {
        event.preventDefault();
        doFill("right");
        return;
      }
      // ⌘V is handled by the paste event — a keydown cannot read the clipboard.
      if (meta) return;

      const from = shift ? selection.extent : ref;
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveTo({ row: from.row - 1, col: from.col }, shift);
          return;
        case "ArrowDown":
          event.preventDefault();
          moveTo({ row: from.row + 1, col: from.col }, shift);
          return;
        case "ArrowLeft":
          event.preventDefault();
          moveTo({ row: from.row, col: from.col - 1 }, shift);
          return;
        case "ArrowRight":
          event.preventDefault();
          moveTo({ row: from.row, col: from.col + 1 }, shift);
          return;
        case "Enter":
          event.preventDefault();
          moveTo({ row: ref.row + (shift ? -1 : 1), col: ref.col }, false);
          return;
        case "Tab":
          event.preventDefault();
          moveTo({ row: ref.row, col: ref.col + (shift ? -1 : 1) }, false);
          return;
        case "Escape": {
          event.preventDefault();
          const row = flatRows[ref.row];
          const month = visibleMonths[ref.col];
          if (!row || month === undefined) return;
          writeCell(row.rowKey, month, serverCells.get(cellKey(row.rowKey, month)) ?? "");
          commit([row.rowKey]);
          return;
        }
        default:
          return;
      }
    },
    [
      doUndo,
      doFill,
      moveTo,
      selection.extent,
      flatRows,
      visibleMonths,
      writeCell,
      serverCells,
      commit,
    ],
  );

  const beginHandleDrag = useCallback(
    (event: React.PointerEvent) => {
      if (hoursReadOnly || steadyState) return;
      event.preventDefault();
      event.stopPropagation();
      const startSelection = selection;

      const cellFromPoint = (clientX: number, clientY: number): CellRef | null => {
        const el = document
          .elementsFromPoint(clientX, clientY)
          .find(
            (node): node is HTMLElement =>
              node instanceof HTMLElement && node.dataset.cell !== undefined,
          );
        const raw = el?.dataset.cell;
        if (raw === undefined) return null;
        const [row, col] = raw.split("-").map(Number);
        if (row === undefined || col === undefined || Number.isNaN(row) || Number.isNaN(col)) {
          return null;
        }
        return { row, col };
      };

      let pending: CellRef | null = null;

      const onMove = (e: PointerEvent) => {
        const ref = cellFromPoint(e.clientX, e.clientY);
        if (!ref) return;
        pending = ref;
        const { extent } = planHandleDrag({
          selection: startSelection,
          pointer: ref,
          rowCount,
          colCount,
        });
        setSelection({ anchor: startSelection.anchor, extent });

        const box = scroller.current?.getBoundingClientRect();
        if (box) {
          const EDGE = 48;
          if (e.clientX > box.right - EDGE) scroller.current?.scrollBy({ left: 24 });
          else if (e.clientX < box.left + geo.frozenWidth + EDGE) {
            scroller.current?.scrollBy({ left: -24 });
          }
        }
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (!pending) return;
        applyTargets(
          planHandleDrag({ selection: startSelection, pointer: pending, rowCount, colCount })
            .targets,
        );
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [hoursReadOnly, steadyState, selection, rowCount, colCount, applyTargets, geo.frozenWidth],
  );

  // ── Totals, deferred ──
  // Ramp factors are saved as they are edited, so the server's months are current.
  const rampMilliByMonth = useMemo(
    () =>
      parseRampMap(
        new Map(
          plan.byMonth.flatMap((m) =>
            m.rampFactor === undefined ? [] : [[m.month, m.rampFactor] as const],
          ),
        ),
      ),
    [plan.byMonth],
  );
  const deferredVersion = useDeferredValue(gridVersion);
  const totals = useMemo(() => {
    void deferredVersion;
    return computeDraftTotals({
      groups,
      months: plan.months,
      lines: plan.lines,
      steadyState,
      readCell,
      readRunRate,
      /*
       * The seeded default, in centi. This is the ONE figure the draft footer does not
       * read from the server, so a workspace that changed `hours_per_fte_month` in
       * Configuration would see the FTE row shift slightly on save — every other draft
       * total is computed the same way the server computes it. Carried as centi to match
       * PlanMetricsInput; see its note on why that quantity is never divided.
       */
      hoursPerFteMonthCenti: 16_000,
      rampMilliByMonth,
    });
  }, [
    deferredVersion,
    groups,
    plan.months,
    plan.lines,
    steadyState,
    readCell,
    readRunRate,
    rampMilliByMonth,
  ]);

  // ── The guidance-load overlay (eye + month load bar) ──
  //
  // Keyed on the plan's SAVED suggested Resources, not the draft: the endpoint reads
  // them from the last save, so a confirm/pick round-trips through `onConfirmWho` /
  // `onOpenWho` and a server refetch before this set moves.
  const suggestedResourceIds = useMemo(
    () =>
      [
        ...new Set(
          plan.lines.flatMap((line) => [
            ...(line.suggestedResourceId === null ? [] : [line.suggestedResourceId]),
            // RP-24: Share holders carry load too.
            ...(line.shares ?? []).map((share) => share.resourceId),
          ]),
        ),
      ].sort(),
    [plan.lines],
  );
  const { data: guidanceLoad } = usePlanGuidanceLoad(plan.id, suggestedResourceIds);

  // Each line's suggested Resource's Fit Score, asked against that line's Discipline. The
  // server returns none for an actor who may not see ratings, so the map is just empty.
  const lineResourceKeys = useMemo(
    () =>
      plan.lines
        .filter((line) => line.suggestedResourceId !== null)
        .map((line) => `${line.id}:${line.suggestedResourceId}`),
    [plan.lines],
  );
  const { data: lineFits } = usePlanLineFits(plan.id, lineResourceKeys);
  const fitByLine = useMemo(
    () => new Map((lineFits ?? []).map((entry) => [entry.lineId, entry.fit])),
    [lineFits],
  );

  // The live, as-you-type plan hours per suggested Resource per month — summed across
  // EVERY line for that Resource, not just this row's. Deferred on the same version as
  // `totals` so a keystroke anywhere does not recompute this on every render.
  const livePlanHoursByResource = useMemo(() => {
    void deferredVersion;
    const hoursOf = (rowKey: string) =>
      visibleMonths.map((month) => ({
        month,
        hours: steadyState ? readRunRate(rowKey) : readCell(rowKey, month),
      }));
    // Every line, not just the visible rows — a collapsed split line still loads its people.
    const liveLines = plan.lines.map((line) => ({
      suggestedResourceId: line.suggestedResourceId,
      months: hoursOf(line.id),
      shares: (line.shares ?? []).map((share) => ({
        resourceId: share.resourceId,
        months: hoursOf(shareRowKey(line.id, share.resourceId)),
      })),
    }));
    return planHoursByResourceMonth(liveLines);
  }, [deferredVersion, plan.lines, visibleMonths, steadyState, readCell, readRunRate]);

  const guidanceByResource = useMemo(() => {
    const map = new Map<string, Map<string, MonthGuidance>>();
    for (const resource of guidanceLoad?.resources ?? []) {
      const byMonth = new Map<string, MonthGuidance>();
      for (const month of resource.months) {
        byMonth.set(month.month, {
          guidanceHours: month.guidanceHours,
          capacityHours: month.capacityHours,
          reservedHours: month.reservedHours,
        });
      }
      map.set(resource.resourceId, byMonth);
    }
    return map;
  }, [guidanceLoad]);

  // In-force Account reservations, by Resource. The server only sends these for Resources the
  // viewer may see (the same set as the load bars), so nothing here widens access.
  const reservationByResource = useMemo(() => {
    const map = new Map<string, ResourcePickerReservationView>();
    for (const resource of guidanceLoad?.resources ?? []) {
      if (resource.reservation !== null) map.set(resource.resourceId, resource.reservation);
    }
    return map;
  }, [guidanceLoad]);

  /**
   * One row's load bars, keyed by Plan Line id — `undefined` means "no eye, no bar"
   * (no suggested Resource, not viewable, or the overlay is still loading). A row WITH
   * an entry always has one element per `visibleMonths`, even when every one of them is
   * `"none"` (guidance unavailable, or no NOS data for that month).
   */
  const rowLoadByLineId = useMemo(() => {
    const out: Record<string, readonly MonthLoad[]> = {};
    if (guidanceLoad === undefined) return out;
    const viewable = new Set(guidanceLoad.viewableResourceIds);
    // Keyed by row key: an unsplit line's bars belong to its Suggested Resource; a split
    // line has none of its own, and each Share row carries its holder's (RP-24).
    const targets: Array<{ rowKey: string; resourceId: string }> = [];
    for (const line of plan.lines) {
      if (isSplitLine(line)) {
        for (const share of line.shares ?? []) {
          targets.push({
            rowKey: shareRowKey(line.id, share.resourceId),
            resourceId: share.resourceId,
          });
        }
      } else if (line.suggestedResourceId !== null) {
        targets.push({ rowKey: line.id, resourceId: line.suggestedResourceId });
      }
    }
    for (const { rowKey, resourceId } of targets) {
      if (!viewable.has(resourceId)) continue;
      const planHours = livePlanHoursByResource.get(resourceId);
      const guidance = guidanceByResource.get(resourceId);
      // Only a reservation for ANOTHER Account spends this plan's open time; the server already
      // zeroed `reservedHours` for this Account's own reservation, so the name is just for the tooltip.
      const reservation = reservationByResource.get(resourceId);
      const reservedForAccountName =
        reservation !== undefined && !reservation.forThisAccount ? reservation.accountName : null;
      out[rowKey] = visibleMonths.map((month) => {
        if (guidanceLoad.guidanceUnavailable) {
          return { tier: "none", tooltip: "NOS guidance is unavailable right now" };
        }
        const monthGuidance = guidance?.get(month) ?? null;
        const load = projectedLoad({
          guidanceHours: monthGuidance?.guidanceHours ?? null,
          capacityHours: monthGuidance?.capacityHours ?? null,
          planHours: planHours?.get(month) ?? 0,
          includesPlanHours: guidanceLoad.includesPlanHours,
          reservedHours: monthGuidance?.reservedHours ?? null,
          reservedForAccountName,
        });
        return { tier: load.tier, tooltip: loadTooltip(monthLabel(month), load) };
      });
    }
    return out;
  }, [
    plan.lines,
    visibleMonths,
    guidanceLoad,
    livePlanHoursByResource,
    guidanceByResource,
    reservationByResource,
  ]);

  /** One row's Resource reservation, keyed by Plan Line id — absent = unreserved or not visible. */
  const rowReservationByLineId = useMemo(() => {
    const out: Record<string, ResourcePickerReservationView> = {};
    for (const { line } of flatRows) {
      if (line.suggestedResourceId === null || isSplitLine(line)) continue;
      const reservation = reservationByResource.get(line.suggestedResourceId);
      if (reservation !== undefined) out[line.id] = reservation;
    }
    return out;
  }, [flatRows, reservationByResource]);

  // ── The unsaved-changes guard ──
  // Navigating away from an unsaved Rate Card silently loses the edit today. For a grid
  // that is not survivable, so this blocks an in-app navigation AND a tab close.
  const dirty = dirtyCount > 0;

  // ── The monthly footer rows (RP-4.1, RP-7.1, RP-D16) ──
  // Margin and cost are the SERVER's figures; they are stale whenever the draft no longer
  // matches what the server last priced — unsaved, saving, or a save that never landed.
  const footStale = dirty || props.saving || !draftMatchesSaved(plan, totals);
  // The viewer can fold the monthly rows away; the Total row always stays.
  const footRows = summaryPref
    ? buildFootRows({ plan, totals, stale: footStale, showMoney, staffingView })
    : [];
  // The Total row sits above the monthly rows, which only exist with months.
  const totalRowBottom = colCount > 0 ? FOOT_SUB_ROW_H * footRows.length : 0;
  useBlocker({
    shouldBlockFn: () => !window.confirm("This plan has unsaved hours. Leave without saving?"),
    enableBeforeUnload: () => dirty,
    disabled: !dirty,
  });

  const save = useCallback(() => {
    // RP-24: Share edits are collected per line and sent as whole Share sets AFTER the line
    // hours, so a Share set is checked against the hours saved with it.
    const shareLineIds = new Set<string>();
    for (const rowKey of dirtyPoints.current) shareLineIds.add(lineIdOfRowKey(rowKey));
    if (steadyState) {
      const runRates: Array<{ planLineId: string; hours: string | null }> = [];
      for (const rowKey of dirtyRunRates.current) {
        if (isShareRowKey(rowKey)) {
          shareLineIds.add(lineIdOfRowKey(rowKey));
          continue;
        }
        const value = runRateDraft.current.get(rowKey) ?? "";
        runRates.push({ planLineId: rowKey, hours: value === "" ? null : value });
      }
      if (runRates.length > 0) props.onSaveRunRates(runRates);
    } else {
      const cells: Array<{ planLineId: string; month: string; hours: string | null }> = [];
      for (const key of dirtyKeys.current) {
        const [rowKey, month] = splitKey(key);
        if (rowKey === null || month === null) continue;
        if (isShareRowKey(rowKey)) {
          shareLineIds.add(lineIdOfRowKey(rowKey));
          continue;
        }
        const value = draft.current.get(key) ?? "";
        cells.push({ planLineId: rowKey, month, hours: value === "" ? null : value });
      }
      if (cells.length > 0) props.onSaveCells(cells);
    }
    if (shareLineIds.size > 0 && props.onSaveShares !== undefined) {
      const sets = [...shareLineIds].flatMap((lineId) => {
        const line = plan.lines.find((l) => l.id === lineId);
        if (line === undefined) return [];
        return [
          {
            planLineId: lineId,
            shares: sharesFromDraft({
              line,
              months: plan.months,
              steadyState,
              readHours: (resourceId, month) =>
                draft.current.get(cellKey(shareRowKey(lineId, resourceId), month)) ?? "",
              readRunRate: (resourceId) =>
                runRateDraft.current.get(shareRowKey(lineId, resourceId)) ?? "",
              readPoints: (resourceId) => {
                const raw = pointsDraft.current.get(shareRowKey(lineId, resourceId)) ?? "";
                const n = Number(raw);
                return raw !== "" && Number.isInteger(n) && n >= 0
                  ? n
                  : (line.shares?.find((x) => x.resourceId === resourceId)?.points ??
                      DEFAULT_SHARE_POINTS);
              },
            }),
          },
        ];
      });
      const last = sets.at(-1);
      // Share edits stay marked unsaved until their write succeeds: a refused set (an
      // over-allocated month, say) must not let the next refetch reseed over the RM's typing.
      const shareKeys = {
        cells: [...dirtyKeys.current].filter((k) => isShareRowKey(splitKey(k)[0] ?? "")),
        runRates: [...dirtyRunRates.current].filter(isShareRowKey),
        points: [...dirtyPoints.current],
      };
      const pending = props.onSaveShares(
        sets.map((set) => (set === last ? { ...set, message: "Split saved" } : set)),
      );
      void pending?.then((ok) => {
        if (!ok) return;
        for (const k of shareKeys.cells) dirtyKeys.current.delete(k);
        for (const k of shareKeys.runRates) dirtyRunRates.current.delete(k);
        for (const k of shareKeys.points) dirtyPoints.current.delete(k);
        setDirtyCount(
          dirtyKeys.current.size + dirtyRunRates.current.size + dirtyPoints.current.size,
        );
      });
    }
    // Line edits are cleared now (their save is in flight); Share edits wait for theirs.
    for (const k of [...dirtyKeys.current]) {
      if (!isShareRowKey(splitKey(k)[0] ?? "")) dirtyKeys.current.delete(k);
    }
    for (const k of [...dirtyRunRates.current]) {
      if (!isShareRowKey(k)) dirtyRunRates.current.delete(k);
    }
    if (props.onSaveShares === undefined) {
      dirtyKeys.current.clear();
      dirtyRunRates.current.clear();
      dirtyPoints.current.clear();
    }
    setDirtyCount(dirtyKeys.current.size + dirtyRunRates.current.size + dirtyPoints.current.size);
    // Undo is session-only and cleared on save: after a save the server is the truth, and
    // "undo" would mean re-writing hours the server has already accepted.
    undo.current.clear();
  }, [steadyState, props, plan.lines, plan.months]);

  // RP-24 — remove one Resource's Share straight away (the whole remaining set is sent).
  const removeShare = useCallback(
    (line: PlanLineView, resourceId: string) => {
      props.onSaveShares?.([
        {
          planLineId: line.id,
          shares: sharesAfterRemoving(line, resourceId, steadyState),
          message: "Resource removed from the line",
        },
      ]);
    },
    [props, steadyState],
  );

  if (plan.months.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        This plan has no months yet. Set a start month, and an end month or a Monthly cadence, on
        the plan setup page.
      </div>
    );
  }

  return (
    <LockedMonthsContext.Provider value={lockedMonths}>
      <div className="flex flex-col gap-2">
        {steadyState && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <span className="font-semibold text-foreground">Monthly run-rate</span>
            <span className="text-muted-foreground">
              {staffingView
                ? "The same hours each month"
                : "Type each line's hours in the Hrs / mo box once — it applies"}{" "}
              across all {plan.months.length} projected month
              {plan.months.length === 1 ? "" : "s"}.
            </span>
            <button
              type="button"
              onClick={() => setShowProjection((v) => !v)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {showProjection ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {showProjection ? "Hide projection" : "Show projection"}
            </button>
          </div>
        )}

        <div
          ref={scroller}
          className="gridscroll relative overflow-auto rounded-md border border-border bg-card"
          style={{ maxHeight: "min(68vh, 720px)" }}
          onPaste={(event) => {
            if (hoursReadOnly || steadyState) return;
            const text = event.clipboardData.getData("text/plain");
            if (!text) return;
            event.preventDefault();
            doPaste(text);
          }}
        >
          <table
            className="border-collapse text-sm"
            style={{ width: geo.frozenWidth + colCount * W_MONTH }}
          >
            <thead>
              <tr ref={headRow}>
                <HeadCell slot={geo.category} align="left">
                  <span className="flex items-center justify-between gap-2">
                    Labor Category
                    <GridViewMenu
                      canViewCost={plan.canViewCost && !staffingView}
                      showMoney={moneyPref}
                      onShowMoney={(next) => {
                        setMoneyPref(next);
                        writeShowMoneyPref(next);
                      }}
                      showSummary={summaryPref}
                      onShowSummary={(next) => {
                        setSummaryPref(next);
                        writeShowSummaryPref(next);
                      }}
                      readOnly={readOnly}
                      onAddWorkstream={props.onAddWorkstream}
                    />
                  </span>
                </HeadCell>
                <HeadCell
                  slot={geo.who}
                  align="left"
                  title="Resource — solid is Confirmed, dashed is Proposed, empty is Open (nobody yet)"
                >
                  Who
                </HeadCell>
                {geo.rate !== null && (
                  <HeadCell
                    slot={geo.rate}
                    align="right"
                    title="Colour shows where the rate came from: violet is a custom rate on this line, blue is the client's rate, black is the list Rate Card. Hover a rate for the detail."
                  >
                    Rate
                  </HeadCell>
                )}
                {geo.runRate !== null && (
                  <HeadCell
                    slot={geo.runRate}
                    align="right"
                    title="Hours per month. Applied to every projected month of the plan."
                  >
                    Hrs / mo
                  </HeadCell>
                )}
                <HeadCell slot={geo.hours} align="right" edge={geo.amount === null}>
                  {steadyState ? "Total" : "Hours"}
                </HeadCell>
                {geo.amount !== null && (
                  <HeadCell slot={geo.amount} align="right" edge={geo.margin === null}>
                    Amount
                  </HeadCell>
                )}
                {geo.cost !== null && (
                  <HeadCell
                    slot={geo.cost}
                    align="right"
                    title="Hours × the line's cost rate — the Labor Category's average cost, or the named Resource's own Internal Rate. As of the last save."
                  >
                    Cost
                  </HeadCell>
                )}
                {geo.margin !== null && (
                  <HeadCell
                    slot={geo.margin}
                    align="right"
                    edge
                    title="The line's indicative margin at its bill rate, against the plan's margin floor. As of the last save."
                  >
                    Margin
                  </HeadCell>
                )}
                {visibleMonths.map((month) => {
                  // The plan's own window, marked where the planner types: the first and
                  // last month read as Start / End without leaving the grid for the header.
                  const edge =
                    month === plan.startMonth ? "Start" : month === plan.endMonth ? "End" : null;
                  const closed = lockedMonths.has(month);
                  return (
                    <th
                      key={month}
                      className={cn(
                        "sticky top-0 z-20 border-l border-border bg-muted px-1.5 py-2 text-right align-bottom text-xs font-semibold whitespace-nowrap uppercase tracking-wider text-muted-foreground tabular-nums",
                        edge !== null &&
                          "bg-accent text-foreground shadow-[inset_0_2px_0_var(--primary)]",
                      )}
                      style={{ width: W_MONTH, minWidth: W_MONTH }}
                      title={
                        closed
                          ? `${monthLabel(month)} — ${CLOSED_MONTH_TITLE}`
                          : edge === null
                            ? undefined
                            : `${edge} period — ${monthLabel(month)}`
                      }
                    >
                      {edge !== null && (
                        <span className="block text-xs font-semibold normal-case tracking-normal text-primary">
                          {edge}
                        </span>
                      )}
                      {closed && (
                        <Lock
                          className="mr-1 inline h-3 w-3 align-[-1px]"
                          aria-label="Closed in NOS"
                        />
                      )}
                      {monthLabel(month)}
                    </th>
                  );
                })}
              </tr>

              {/*
               * RP-4.3's ramp row — "above the grid", which in a two-axis-frozen table means
               * a second header row: it has to SCROLL WITH the month columns, and anything
               * rendered outside the scroll container would drift out of alignment the
               * moment the planner scrolled to month 14.
               *
               * Hidden in Steady-State mode: that mode holds one run-rate figure per line
               * with no month cells to de-rate (RP-4.4), so a per-month factor has nothing
               * to multiply.
               */}
              {!steadyState && props.onSetRamp !== undefined && (
                <RampRow
                  geo={geo}
                  months={visibleMonths}
                  byMonth={plan.byMonth}
                  canEdit={canEditContent}
                  onSetRamp={props.onSetRamp}
                  top={headRowH}
                />
              )}
            </thead>

            <tbody>
              {groups.map((group) => (
                <GroupBlock
                  key={group.workstream.id ?? "default"}
                  geo={geo}
                  group={group}
                  plan={plan}
                  months={visibleMonths}
                  totals={totals}
                  rect={rect}
                  rowVersion={rowVersion}
                  steadyState={steadyState}
                  readCell={readCell}
                  readRunRate={readRunRate}
                  writeCell={writeCell}
                  writeRunRate={writeRunRate}
                  commit={commit}
                  onCellKeyDown={onCellKeyDown}
                  /*
                   * Focus and selection are kept in step WITHOUT a one-shot flag.
                   *
                   * Extending with Shift+arrow has to move focus to the new extent, and
                   * moving focus fires this handler — so a handler that unconditionally
                   * collapsed would make Shift+arrow impossible, silently breaking ⌘D and
                   * ⌘R. An earlier attempt used a suppress-once ref, which LEAKS: if focus
                   * does not actually move the flag stays raised and corrupts the next click.
                   *
                   * The containment rule has no such state. `moveTo` has already queued the
                   * extended selection before calling focus(), and a functional updater sees
                   * that queued value — so during an extend the new cell is inside the
                   * selection and nothing collapses. Focus arriving from OUTSIDE the
                   * selection (a Tab into the grid) collapses onto it, which is right.
                   */
                  onCellFocus={(ref) =>
                    setSelection((prev) =>
                      rectContains(selectionRect(prev), ref.row, ref.col)
                        ? prev
                        : { anchor: ref, extent: ref },
                    )
                  }
                  onCellMouseDown={(ref, shift) =>
                    setSelection((prev) =>
                      shift ? { anchor: prev.anchor, extent: ref } : { anchor: ref, extent: ref },
                    )
                  }
                  onHandleDown={beginHandleDrag}
                  readOnly={readOnly}
                  ratesReadOnly={ratesReadOnly}
                  hoursReadOnly={hoursReadOnly}
                  resourcesReadOnly={resourcesReadOnly}
                  onAddLine={props.onAddLine}
                  addingLine={props.addingLine}
                  onOpenLine={props.onOpenLine}
                  onOpenWorkstream={props.onOpenWorkstream}
                  onOpenWho={props.onOpenWho}
                  onConfirmWho={props.onConfirmWho}
                  onSetLineStatus={props.onSetLineStatus}
                  onEditRate={props.onEditRate}
                  onSizeWorkstream={props.onSizeWorkstream}
                  rowLoadByLineId={rowLoadByLineId}
                  rowReservationByLineId={rowReservationByLineId}
                  fitByLine={fitByLine}
                  onViewResource={props.onViewResource}
                  expanded={expanded}
                  onToggleExpand={toggleExpanded}
                  shareRowsByLine={shareRowsByLine}
                  canManageShares={canManageShares}
                  maxShares={maxShares}
                  readPoints={readPoints}
                  writePoints={writePoints}
                  onAddShareResource={props.onAddShareResource}
                  onRemoveShare={removeShare}
                  onOpenCandidates={props.onOpenCandidates}
                  onOpenNotes={props.onOpenNotes}
                />
              ))}
            </tbody>

            <tfoot>
              <tr>
                <FootCell
                  left={0}
                  width={geo.hours.left}
                  // Everything before Hours: Hours and (when shown) Amount, Cost and Margin
                  // each get a cell of their own after this one.
                  colSpan={
                    geo.frozenCols - 1 - (geo.amount === null ? 0 : 1) - (geo.cost === null ? 0 : 2)
                  }
                  align="left"
                  bottom={totalRowBottom}
                >
                  Total ·{" "}
                  {plan.workstreams.filter((w) => w.id !== null).length > 0
                    ? `${plan.workstreams.filter((w) => w.id !== null).length} Workstreams · `
                    : ""}
                  {plan.lines.length} Plan Line{plan.lines.length === 1 ? "" : "s"}
                </FootCell>
                <FootCell
                  slot={geo.hours}
                  align="right"
                  strong
                  edge={geo.amount === null}
                  bottom={totalRowBottom}
                >
                  {fmtNum(totals.planHours)}
                </FootCell>
                {geo.amount !== null && (
                  <FootCell
                    slot={geo.amount}
                    align="right"
                    strong
                    edge={geo.margin === null}
                    bottom={totalRowBottom}
                  >
                    {fmtMoney(totals.planAmount)}
                  </FootCell>
                )}
                {geo.cost !== null && (
                  <FootCell slot={geo.cost} align="right" strong bottom={totalRowBottom}>
                    {plan.totalCost === undefined ? "—" : fmtMoney(plan.totalCost)}
                  </FootCell>
                )}
                {geo.margin !== null && (
                  <FootCell slot={geo.margin} align="right" strong edge bottom={totalRowBottom}>
                    <MarginFigure percent={plan.indicativeMarginPercent} plan={plan} />
                  </FootCell>
                )}
                {visibleMonths.map((month) => (
                  <td
                    key={month}
                    className="sticky z-20 border-l border-t-2 border-border bg-muted px-1.5 py-2 text-right text-sm font-semibold tabular-nums"
                    style={{ width: W_MONTH, bottom: totalRowBottom }}
                    title={afterRampTitle(totals, month)}
                  >
                    {fmtNum(totals.byMonthHours[month] ?? "0.00")}
                  </td>
                ))}
              </tr>
              {colCount > 0 &&
                footRows.map((row, i) => {
                  // Each footer row needs its OWN bottom offset — see FOOT_SUB_ROW_H.
                  const bottom = (footRows.length - 1 - i) * FOOT_SUB_ROW_H;
                  return (
                    <tr key={row.key}>
                      <FootCell
                        left={0}
                        width={geo.frozenWidth}
                        colSpan={geo.frozenCols}
                        align="left"
                        muted
                        edge
                        bottom={bottom}
                        height={FOOT_SUB_ROW_H}
                      >
                        {row.label}
                        <span className="ml-1 cursor-help text-muted-foreground/70" title={row.tip}>
                          ⓘ
                        </span>
                      </FootCell>
                      {visibleMonths.map((month) => {
                        const cell = row.cell(month);
                        return (
                          <td
                            key={month}
                            className={cn(
                              // The background stays OPAQUE (see the sticky header note) —
                              // a stale row dims its text, never its cell.
                              "sticky z-20 border-l border-border bg-muted px-1.5 text-right text-xs tabular-nums",
                              row.stale ? "text-muted-foreground/60" : row.className,
                            )}
                            style={{ width: W_MONTH, bottom, height: FOOT_SUB_ROW_H }}
                            title={cell.title}
                          >
                            {cell.text}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tfoot>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-muted-foreground">
          {steadyState ? (
            <span>
              One figure per line, applied to every projected month. Use &ldquo;Edit setup&rdquo;
              above to switch to month-by-month entry if a month needs to differ.
            </span>
          ) : (
            <>
              <span>Arrows move · Enter down · Tab right</span>
              <span aria-hidden>·</span>
              <span className="font-medium text-foreground/70">⌘V pastes a month block</span>
              <span aria-hidden>·</span>
              <span>⌘D fill down · ⌘R fill right</span>
              <span aria-hidden>·</span>
              <span className="font-medium text-foreground/70">drag the corner handle to fill</span>
              <span aria-hidden>·</span>
              <span>⌘Z undo</span>
            </>
          )}
          <span className="ml-auto flex items-center gap-2">
            {dirty ? (
              <>
                <span className="font-medium text-warning-700 dark:text-warning-400">
                  {dirtyCount} unsaved {steadyState ? "line" : "cell"}
                  {dirtyCount === 1 ? "" : "s"}
                </span>
                <Button size="sm" onClick={save} disabled={props.saving}>
                  {props.saving ? "Saving…" : "Save hours"}
                </Button>
              </>
            ) : (
              <span>{props.saving ? "Saving…" : "All changes saved"}</span>
            )}
          </span>
        </div>
      </div>
    </LockedMonthsContext.Provider>
  );
}

function splitKey(key: string): [string | null, string | null] {
  const at = key.lastIndexOf(":");
  if (at <= 0) return [null, null];
  return [key.slice(0, at), key.slice(at + 1)];
}

// ── Totals over the DRAFT ─────────────────────────────────────────────────────

interface DraftTotals {
  /** ENTERED hours, so the Total row still sums the cells above it. */
  planHours: string;
  /** Amounts are EFFECTIVE — entered × ramp — exactly as the server prices them (RP-4.3). */
  planAmount: string;
  /** ENTERED hours per month — the column sum of the cells. */
  byMonthHours: Record<string, string>;
  /** EFFECTIVE hours per month, in centi — the Fee Spread's weights and the FTE basis. */
  byMonthEffectiveCenti: Record<string, number>;
  /** Revenue per month — effective hours × bill rate, summed across every line. */
  byMonthAmount: Record<string, string>;
  byMonthFte: Record<string, string>;
  byLineHours: Record<string, string>;
  byLineAmount: Record<string, string>;
  byWorkstreamHours: Record<string, string>;
  byWorkstreamMonth: Record<string, Record<string, string>>;
}

/**
 * Recompute the visible totals from the DRAFT, not the server payload.
 *
 * The server owns the authoritative arithmetic (src/lib/resource-plan-math.ts, integer
 * micros end to end). This is the optimistic echo that keeps the footer honest between
 * keystroke and save, and it uses the same integer scales — hours in hundredths, money in
 * micros — so the two never disagree by a rounding step.
 *
 * In steady-state each line's single run-rate is expanded across every month, exactly as
 * the server does, so the footer reads the same before and after a save.
 */
function computeDraftTotals(input: {
  groups: GroupModel[];
  months: readonly string[];
  lines: readonly PlanLineView[];
  steadyState: boolean;
  readCell: (lineId: string, month: string) => string;
  readRunRate: (lineId: string) => string;
  /** Hours per FTE-month in CENTI — hours × 100, an exact integer. */
  hoursPerFteMonthCenti: number;
  /** month → ramp milli (RP-4.3). Absent means 1.0. */
  rampMilliByMonth: ReadonlyMap<string, number>;
}): DraftTotals {
  const rateCentsByLine = new Map<string, number>();
  for (const line of input.lines) rateCentsByLine.set(line.id, toMinor(line.billRate));

  let planHoursCenti = 0;
  let planAmountMicros = 0;
  const byMonth = new Map<string, number>();
  const byMonthEffective = new Map<string, number>();
  const byMonthAmountMicros = new Map<string, number>();
  const byLineHoursCenti = new Map<string, number>();
  const byLineAmountMicros = new Map<string, number>();
  const byWorkstream = new Map<string, number>();
  const byWorkstreamMonth = new Map<string, Map<string, number>>();

  for (const group of input.groups) {
    const key = group.workstream.id ?? "default";
    let wsHours = 0;
    const wsMonths = new Map<string, number>();

    for (const { line } of group.rows) {
      const rateCents = rateCentsByLine.get(line.id) ?? 0;
      let lineHours = 0;
      let lineEffective = 0;
      // One cell: hours as typed for the hour totals, ramped for every money total — the
      // server's own order (expandLineCells), so draft and saved figures agree to the cent.
      const addCell = (month: string, enteredCenti: number) => {
        const effective = rampHoursCenti(enteredCenti, input.rampMilliByMonth.get(month) ?? 1_000);
        lineHours += enteredCenti;
        lineEffective += effective;
        byMonth.set(month, (byMonth.get(month) ?? 0) + enteredCenti);
        byMonthEffective.set(month, (byMonthEffective.get(month) ?? 0) + effective);
        byMonthAmountMicros.set(
          month,
          (byMonthAmountMicros.get(month) ?? 0) + rateCents * effective * 100,
        );
        wsMonths.set(month, (wsMonths.get(month) ?? 0) + enteredCenti);
      };

      if (input.steadyState) {
        const raw = input.readRunRate(line.id);
        if (raw !== "") {
          const perMonth = toMinor(raw);
          for (const month of input.months) addCell(month, perMonth);
        }
      } else {
        for (const month of input.months) {
          const raw = input.readCell(line.id, month);
          if (raw === "") continue;
          addCell(month, toMinor(raw));
        }
      }

      byLineHoursCenti.set(line.id, lineHours);
      byLineAmountMicros.set(line.id, rateCents * lineEffective * 100);
      planHoursCenti += lineHours;
      planAmountMicros += rateCents * lineEffective * 100;
      wsHours += lineHours;
    }

    byWorkstream.set(key, wsHours);
    byWorkstreamMonth.set(key, new Map([...wsMonths].map(([m, v]) => [m, v])));
  }

  const hoursPerFteCenti = input.hoursPerFteMonthCenti;

  return {
    planHours: fromCenti(planHoursCenti),
    planAmount: fromMicros(planAmountMicros),
    byMonthHours: Object.fromEntries(input.months.map((m) => [m, fromCenti(byMonth.get(m) ?? 0)])),
    byMonthEffectiveCenti: Object.fromEntries(
      input.months.map((m) => [m, byMonthEffective.get(m) ?? 0]),
    ),
    byMonthAmount: Object.fromEntries(
      input.months.map((m) => [m, fromMicros(byMonthAmountMicros.get(m) ?? 0)]),
    ),
    byMonthFte: Object.fromEntries(
      input.months.map((m) => [
        m,
        hoursPerFteCenti === 0
          ? "0.00"
          : fromCenti(Math.round(((byMonthEffective.get(m) ?? 0) * 100) / hoursPerFteCenti)),
      ]),
    ),
    byLineHours: Object.fromEntries([...byLineHoursCenti].map(([id, v]) => [id, fromCenti(v)])),
    byLineAmount: Object.fromEntries([...byLineAmountMicros].map(([id, v]) => [id, fromMicros(v)])),
    byWorkstreamHours: Object.fromEntries([...byWorkstream].map(([id, v]) => [id, fromCenti(v)])),
    byWorkstreamMonth: Object.fromEntries(
      [...byWorkstreamMonth].map(([id, months]) => [
        id,
        Object.fromEntries([...months].map(([m, v]) => [m, fromCenti(v)])),
      ]),
    ),
  };
}

// ── The monthly footer rows ───────────────────────────────────────────────────

interface FootRow {
  key: string;
  label: string;
  tip: string;
  /** Read from the last SAVE, so it dims while the grid has unsaved hours. */
  stale: boolean;
  className?: string;
  cell: (month: string) => { text: string; title?: string };
}

/**
 * The rows under the Total row, top to bottom: what the month earns, its margin, its cost
 * (viewPlanCost only — the server never sends it otherwise) and FTE.
 *
 * Unticking "Show cost & margin" hides the margin and cost rows too, not just the line columns —
 * the toggle is how a cost viewer screen-shares a plan, so a figure left in the footer
 * defeats it. `showMoney` is false for every non-cost viewer as well, but they have no
 * toggle and always saw margin, so their margin row is kept.
 *
 * What a month EARNS depends on the fee (src/lib/fee-spread.ts): hours × rate on T&M, the
 * fee itself on a Monthly-basis fee, the Fee Spread on Fixed Price × Total. That row and
 * FTE are recomputed live from the draft. Margin and cost come from the server — the grid
 * cannot price a Resource's Internal Rate months, nor a line whose cost it is not sent — so
 * while hours are unsaved they dim and say so rather than pass an old figure off as new.
 */
function buildFootRows(input: {
  plan: ResourcePlanDetailView;
  totals: DraftTotals;
  stale: boolean;
  showMoney: boolean;
  /** The staffing view carries no money at all — FTE only. */
  staffingView: boolean;
}): FootRow[] {
  const { plan, totals } = input;
  const moneyHidden = plan.canViewCost && !input.showMoney;
  const basis = monthRevenueBasis(plan);
  const saved = new Map(plan.byMonth.map((m) => [m.month, m]));
  const spread =
    basis === "fee-spread" && plan.contractedFee !== null
      ? spreadByWeights(
          toMinor(plan.contractedFee),
          plan.months.map((m) => totals.byMonthEffectiveCenti[m] ?? 0),
        )
      : null;
  const monthIndex = new Map(plan.months.map((m, i) => [m, i]));
  const staleNote = input.stale ? " Showing the last saved figure — save to update." : "";

  const earns: FootRow =
    basis === "monthly-fee"
      ? {
          key: "earns",
          label: "Contracted Fee per month",
          tip: "The Monthly Contracted Fee — what this plan earns each month. What the hours are worth at our own rates is in the Amount column.",
          stale: false,
          className: "font-semibold",
          cell: () => ({
            text: fmtWholeMoney(plan.contractedFee ?? "0.00"),
            title: fmtMoney(plan.contractedFee ?? "0.00"),
          }),
        }
      : basis === "fee-spread"
        ? {
            key: "earns",
            label: "Fee Spread per month",
            tip: "The Contracted Fee spread across months in proportion to each month's planned hours (after ramp). A forecast for planning, not a billing schedule.",
            stale: false,
            className: "font-semibold",
            cell: (month) => {
              const cents = spread?.[monthIndex.get(month) ?? -1];
              if (cents === undefined)
                return { text: "—", title: "No hours to spread the fee on yet." };
              const value = fromCenti(cents);
              return { text: fmtWholeMoney(value), title: fmtMoney(value) };
            },
          }
        : {
            key: "earns",
            label: "Revenue per month",
            tip: "Hours after ramp × bill rate for the month, across every Plan Line. Rounded to the dollar; the Amount column carries the exact figure.",
            stale: false,
            className: "font-semibold",
            cell: (month) => {
              const value = totals.byMonthAmount[month] ?? "0.00";
              return { text: fmtWholeMoney(value), title: fmtMoney(value) };
            },
          };

  const margin: FootRow = {
    key: "margin",
    // Named for what it is measured on: beside a line Margin column that is Indicative, an
    // unlabelled Contracted figure would read as the two disagreeing.
    label: basis === "indicative" ? "Margin per month" : "Contracted margin per month",
    tip:
      (basis === "indicative"
        ? "Margin on the month's revenue at bill rates, before any plan-level discount."
        : "Margin on the month's share of the Contracted Fee.") + staleNote,
    stale: input.stale,
    cell: (month) => {
      const pct = saved.get(month)?.marginPercent ?? null;
      return { text: pct === null ? "—" : `${Number(pct).toFixed(1)}%` };
    },
  };

  const rows: FootRow[] = input.staffingView ? [] : moneyHidden ? [earns] : [earns, margin];

  if (input.showMoney) {
    rows.push({
      key: "cost",
      label: "Cost per month",
      tip: `Planned cost for the month. Hover a month for its net.${staleNote}`,
      stale: input.stale,
      cell: (month) => {
        const m = saved.get(month);
        if (m?.totalCost === undefined) return { text: "—" };
        return {
          text: fmtWholeMoney(m.totalCost),
          title: `Cost ${fmtMoney(m.totalCost)} · Net ${fmtMoney(m.totalNet ?? "0.00")}`,
        };
      },
    });
  }

  rows.push({
    key: "fte",
    label: "FTE per month",
    tip: "A reference figure: hours after ramp ÷ Hours per FTE-month. Never written to Intensity or points.",
    stale: false,
    className: "text-muted-foreground",
    cell: (month) => ({ text: totals.byMonthFte[month] ?? "0.00" }),
  });

  return rows;
}

/**
 * Whether the server's month figures were priced from the hours the grid now shows. Both
 * sides compute effective hours and revenue the same way, to the cent, so any difference
 * means the server has not seen this draft yet.
 */
function draftMatchesSaved(plan: ResourcePlanDetailView, totals: DraftTotals): boolean {
  return plan.byMonth.every(
    (m) =>
      toMinor(m.totalHours) === (totals.byMonthEffectiveCenti[m.month] ?? 0) &&
      m.totalRevenue === (totals.byMonthAmount[m.month] ?? "0.00"),
  );
}

/** On a ramped month, say what the column's hours come to once the ramp applies. */
function afterRampTitle(totals: DraftTotals, month: string): string | undefined {
  const entered = toMinor(totals.byMonthHours[month] ?? "0.00");
  const effective = totals.byMonthEffectiveCenti[month] ?? 0;
  return entered === effective ? undefined : `After ramp: ${fmtNum(fromCenti(effective))} hours`;
}

/** `"160.5"` → 16050. The client-side twin of parseMinor(value, 2). */
function toMinor(value: string): number {
  const [whole = "0", frac = ""] = value.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0").slice(0, 2) || "0");
}

function fromCenti(centi: number): string {
  return `${Math.trunc(centi / 100)}.${String(Math.abs(centi % 100)).padStart(2, "0")}`;
}

function fromMicros(micros: number): string {
  const cents = Math.round(micros / 10_000);
  return `${Math.trunc(cents / 100)}.${String(Math.abs(cents % 100)).padStart(2, "0")}`;
}

// ── Sticky cell helpers ───────────────────────────────────────────────────────

export function HeadCell(props: {
  slot: Slot;
  align: "left" | "right" | "center";
  edge?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <th
      // The corner cells: sticky on BOTH axes, and the highest z in the grid so they win
      // over the sticky month header AND the sticky frozen columns.
      className={cn(
        "sticky top-0 z-30 bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        props.align === "right" && "text-right",
        props.align === "center" && "text-center",
        props.align === "left" && "text-left",
        props.edge === true && "border-r-2 border-border",
        props.title !== undefined && "cursor-help",
      )}
      style={{ left: props.slot.left, width: props.slot.width, minWidth: props.slot.width }}
      title={props.title}
    >
      {props.children}
    </th>
  );
}

export function FootCell(props: {
  slot?: Slot;
  left?: number;
  width?: number;
  align: "left" | "right";
  colSpan?: number;
  strong?: boolean;
  muted?: boolean;
  edge?: boolean;
  /** Sticky offset from the bottom — see FOOT_SUB_ROW_H. Defaults to 0. */
  bottom?: number;
  height?: number;
  children: React.ReactNode;
}) {
  const left = props.slot?.left ?? props.left ?? 0;
  const width = props.slot?.width ?? props.width ?? 0;
  return (
    <td
      colSpan={props.colSpan}
      className={cn(
        "sticky z-30 border-t-2 border-border bg-muted px-3 py-2 tabular-nums",
        props.align === "right" ? "text-right" : "text-left",
        props.strong === true ? "text-sm font-bold" : "text-sm font-semibold",
        props.muted === true &&
          "border-t-0 py-0 text-xs uppercase tracking-wider text-muted-foreground",
        props.edge === true && "border-r-2",
      )}
      style={{ left, width, bottom: props.bottom ?? 0, height: props.height }}
    >
      {props.children}
    </td>
  );
}

// ── A Workstream and its rows ─────────────────────────────────────────────────

interface GroupBlockProps {
  geo: Geo;
  group: GroupModel;
  plan: ResourcePlanDetailView;
  months: readonly string[];
  totals: DraftTotals;
  rect: { top: number; bottom: number; left: number; right: number };
  rowVersion: Record<string, number>;
  steadyState: boolean;
  readCell: (lineId: string, month: string) => string;
  readRunRate: (lineId: string) => string;
  writeCell: (lineId: string, month: string, value: string) => CellPatch;
  writeRunRate: (lineId: string, value: string) => void;
  commit: (touched: Iterable<string>) => void;
  onCellKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, ref: CellRef) => void;
  onCellFocus: (ref: CellRef) => void;
  onCellMouseDown: (ref: CellRef, shift: boolean) => void;
  onHandleDown: (event: React.PointerEvent) => void;
  readOnly: boolean;
  /** An existing line's bill rate — Draft only, even when content is open after Won. */
  ratesReadOnly: boolean;
  /** Hours cells and the run-rate — also editable by an RM staffing a sent plan. */
  hoursReadOnly: boolean;
  /** The Who column and status chip — editable past Draft. */
  resourcesReadOnly: boolean;
  onAddLine: PlanGridProps["onAddLine"];
  addingLine: boolean;
  onOpenLine: (line: PlanLineView) => void;
  onOpenWorkstream: (workstream: WorkstreamView) => void;
  onOpenWho: (line: PlanLineView, anchor: HTMLElement) => void;
  onConfirmWho: (line: PlanLineView) => void;
  onSetLineStatus: PlanGridProps["onSetLineStatus"];
  onEditRate: (line: PlanLineView, anchor: HTMLElement) => void;
  onSizeWorkstream?: PlanGridProps["onSizeWorkstream"];
  /** See `PlanGrid`'s `rowLoadByLineId` memo for how this is built. */
  rowLoadByLineId: Record<string, readonly MonthLoad[]>;
  /** See `PlanGrid`'s `rowReservationByLineId` memo. */
  rowReservationByLineId: Record<string, ResourcePickerReservationView>;
  /** Each line's suggested Resource's Fit Score; absent = not scored or not viewable. */
  fitByLine: ReadonlyMap<string, ResourcePickerFitView>;
  onViewResource?: PlanGridProps["onViewResource"];
  // ── RP-24 Line Shares ──
  expanded: ReadonlySet<string>;
  onToggleExpand: (lineId: string) => void;
  shareRowsByLine: ReadonlyMap<string, GridRowModel[]>;
  canManageShares: boolean;
  maxShares: number;
  readPoints: (rowKey: string) => string;
  writePoints: (rowKey: string, value: string) => void;
  onAddShareResource?: PlanGridProps["onAddShareResource"];
  onRemoveShare: (line: PlanLineView, resourceId: string) => void;
  onOpenCandidates?: PlanGridProps["onOpenCandidates"];
  onOpenNotes?: PlanGridProps["onOpenNotes"];
}

function GroupBlock(props: GroupBlockProps) {
  const { geo, group, plan, totals, months } = props;
  const ws = group.workstream;
  const key = ws.id ?? "default";
  const hours = totals.byWorkstreamHours[key] ?? "0.00";
  const isDefault = ws.id === null;
  const [adding, setAdding] = useState(false);

  // The default group's header is deliberately quiet: it is a bucket, not a category, and
  // dressing it up as a Workstream would imply the plan needs grouping it does not.
  return (
    <>
      <tr>
        <td
          colSpan={geo.frozenCols}
          className={cn(
            "sticky left-0 z-[15] border-y border-r-2 border-border px-3 py-1.5",
            isDefault ? "bg-muted/20" : "bg-muted/40",
          )}
          style={{ width: geo.frozenWidth }}
        >
          <div className="flex items-center gap-2">
            {!isDefault && <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />}
            <span
              className={cn(
                "truncate",
                isDefault
                  ? "text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  : "text-sm font-semibold",
              )}
              title={
                isDefault
                  ? "Plan Lines that are not grouped into a Workstream. Workstreams are optional."
                  : undefined
              }
            >
              {ws.name}
            </span>
            {ws.type !== null && <WorkstreamTypeChip type={ws.type} />}
            {ws.lineCount > 0 && (
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {ws.lineCount} line{ws.lineCount === 1 ? "" : "s"} · {fmtNum(hours)} hrs
              </span>
            )}
            {!isDefault && <EstimateChip workstream={ws} />}
            {/*
             * RP-3.2's "Template Hours vs Entered Hours". Separate from the Estimate chip
             * beside it, deliberately — they measure different things, and conflating
             * "drifted from the template" with "drifted from the estimate" is exactly the
             * confusion the three-figure distinction exists to prevent.
             */}
            {!isDefault && <TemplateDriftChip workstream={ws} />}
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {!props.readOnly && !isDefault && props.onSizeWorkstream !== undefined && (
                <button
                  type="button"
                  onClick={() => props.onSizeWorkstream?.(ws)}
                  className="inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  title="Size this Workstream from its template, or override its mix"
                >
                  <Ruler className="h-2.5 w-2.5" />
                  Size
                </button>
              )}
              {!props.readOnly && !isDefault && (
                <button
                  type="button"
                  onClick={() => props.onOpenWorkstream(ws)}
                  aria-label={`Edit the ${ws.name} Workstream`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground transition-colors hover:text-foreground"
                >
                  <MoreVertical className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </td>
        {months.map((month) => (
          <td
            key={month}
            className={cn(
              "border-y border-l border-border px-1.5 py-1.5 text-right text-xs text-muted-foreground tabular-nums",
              isDefault ? "bg-muted/20" : "bg-muted/40",
            )}
            style={{ width: W_MONTH }}
          >
            {subtotal(totals, key, month)}
          </td>
        ))}
      </tr>

      {group.rows.map(({ line, rowIndex }) => (
        <Fragment key={line.id}>
          <GridRow
            key={line.id}
            geo={geo}
            line={line}
            plan={props.plan}
            rowIndex={rowIndex}
            months={months}
            version={props.rowVersion[line.id] ?? 0}
            steadyState={props.steadyState}
            totalHours={totals.byLineHours[line.id] ?? "0.00"}
            totalAmount={totals.byLineAmount[line.id] ?? "0.00"}
            runRate={props.readRunRate(line.id)}
            rect={props.rect}
            readCell={props.readCell}
            writeCell={props.writeCell}
            writeRunRate={props.writeRunRate}
            commit={props.commit}
            onCellKeyDown={props.onCellKeyDown}
            onCellFocus={props.onCellFocus}
            onCellMouseDown={props.onCellMouseDown}
            onHandleDown={props.onHandleDown}
            readOnly={props.readOnly}
            ratesReadOnly={props.ratesReadOnly}
            hoursReadOnly={props.hoursReadOnly}
            resourcesReadOnly={props.resourcesReadOnly}
            onOpenLine={props.onOpenLine}
            onOpenWho={props.onOpenWho}
            onConfirmWho={props.onConfirmWho}
            onSetLineStatus={props.onSetLineStatus}
            onEditRate={props.onEditRate}
            rowLoad={props.rowLoadByLineId[line.id]}
            reservation={props.rowReservationByLineId[line.id]}
            fit={props.fitByLine.get(line.id)}
            onViewResource={props.onViewResource}
            expanded={props.expanded.has(line.id)}
            onToggleExpand={props.onToggleExpand}
            onOpenCandidates={props.onOpenCandidates}
            onOpenNotes={props.onOpenNotes}
          />
          {props.expanded.has(line.id) && (
            <LineSubtree
              geo={geo}
              line={line}
              months={months}
              steadyState={props.steadyState}
              shareRows={props.shareRowsByLine.get(line.id) ?? []}
              rowVersion={props.rowVersion}
              rect={props.rect}
              readCell={props.readCell}
              readRunRate={props.readRunRate}
              writeCell={props.writeCell}
              writeRunRate={props.writeRunRate}
              commit={props.commit}
              onCellKeyDown={props.onCellKeyDown}
              onCellFocus={props.onCellFocus}
              onCellMouseDown={props.onCellMouseDown}
              onHandleDown={props.onHandleDown}
              canManageShares={props.canManageShares}
              maxShares={props.maxShares}
              readPoints={props.readPoints}
              writePoints={props.writePoints}
              onAddShareResource={props.onAddShareResource}
              onOpenWho={props.onOpenWho}
              onRemoveShare={props.onRemoveShare}
              rowLoadByKey={props.rowLoadByLineId}
            />
          )}
        </Fragment>
      ))}

      {adding && !props.readOnly && (
        <AddLineRow
          geo={geo}
          plan={plan}
          months={months}
          steadyState={props.steadyState}
          workstreamId={ws.id}
          pending={props.addingLine}
          onAdd={(input) => {
            props.onAddLine({ workstreamId: ws.id, ...input });
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {/*
       * Adding a line is THE repeated action on this page, so its affordance sits where
       * the new line will land — directly under the last row of the group — rather than
       * as a 10px chip in the group header, where it read as a header control and was
       * missed. It is the loudest thing in the block on purpose; "Add Workstream" lives in
       * the grid's ⋯ menu, because grouping is the rarer decision.
       */}
      {!adding && !props.readOnly && (
        <tr>
          <td
            colSpan={geo.frozenCols}
            className="sticky left-0 z-10 border-b border-r-2 border-border bg-card p-0"
            style={{ width: geo.frozenWidth }}
          >
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex w-full items-center gap-2 border-y border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-left text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Plus className="h-3 w-3" />
              </span>
              Add Plan Line
              {group.rows.length === 0 && (
                <span className="truncate font-normal text-muted-foreground">
                  {isDefault ? "— nothing priced yet" : `— nothing in ${ws.name} yet`}
                </span>
              )}
            </button>
          </td>
          {months.length > 0 && <td colSpan={months.length} className="border-b border-border" />}
        </tr>
      )}

      {group.rows.length === 0 && !adding && props.readOnly && (
        <tr>
          <td
            colSpan={geo.frozenCols}
            className="sticky left-0 z-10 border-b border-r-2 border-border bg-card px-3 py-2 text-sm text-muted-foreground"
            style={{ width: geo.frozenWidth }}
          >
            {isDefault
              ? "No Plan Lines yet — add one to start pricing the work."
              : "No Plan Lines in this Workstream yet."}
          </td>
          {months.length > 0 && <td colSpan={months.length} className="border-b border-border" />}
        </tr>
      )}
    </>
  );
}

/** A Workstream header's type chip — here and on the shared Plan Overview's grid. */
export function WorkstreamTypeChip({ type }: { type: string }) {
  return (
    <span className="shrink-0 rounded bg-background px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
      {type === "RunSupport" ? "Run / Support" : type}
    </span>
  );
}

function subtotal(totals: DraftTotals, groupKey: string, month: string): string {
  const value = totals.byWorkstreamMonth[groupKey]?.[month];
  if (value === undefined || value === "0.00") return "";
  return fmtNum(value);
}

/** The drift chip on a Workstream header — where the editing actually happens. */
function EstimateChip({ workstream }: { workstream: WorkstreamView }) {
  const band = workstream.estimateBand;
  if (band === "no-estimate") {
    return (
      <span
        className="shrink-0 cursor-help rounded px-1.5 py-0.5 text-xs font-semibold text-muted-foreground/70"
        title="No estimate recorded for this Workstream. Add one on the Estimates panel."
      >
        no estimate
      </span>
    );
  }
  const pct = workstream.estimateVariancePercent;
  const label =
    band === "reconciled"
      ? "reconciled"
      : `${pct !== null && !pct.startsWith("-") ? "+" : "−"}${pct?.replace("-", "")}% vs est`;
  return (
    <span
      className={cn(
        "shrink-0 cursor-help rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums",
        band === "reconciled" && "bg-success-100 text-success-700 dark:text-success-400",
        band === "warn" && "bg-warning-100 text-warning-700 dark:text-warning-400",
        band === "red" && "bg-error-100 text-error-700 dark:text-error-400",
      )}
      title={`Estimated ${workstream.estimatedHours ?? "—"} h · planned ${workstream.totalHours} h. The estimate never drives the plan — it is what the plan is checked against.`}
    >
      {label}
    </span>
  );
}

// ── The inline add row (no dialog) ────────────────────────────────────────────

/**
 * Adding a Plan Line, inline in the grid.
 *
 * A dialog for this was wrong: adding a line is the most repeated action on the page, and
 * a modal makes every one of them a context switch. The row sits where the new line will
 * land, prefills the CARD RATE for whichever Labor Category is picked (`plan.cardRates`,
 * bill rates only — no cost), and takes the run-rate directly on a steady-state plan so a
 * retainer line is one action rather than "add, then go and type a number".
 *
 * Enter adds, Escape cancels.
 */
/**
 * RP-3.2's drift chip: how far the grid has moved from what the template distributed.
 *
 * Absent when the Workstream was never sized from a template, and when it has not drifted
 * — a chip that says "0%" on every unsized Workstream is noise. Deliberately NEUTRAL in
 * color: drifting from a template is normal authoring, not an error. The Estimate chip
 * beside it is the one with bands, because drifting from an ESTIMATE is a signal about
 * scope.
 */
function TemplateDriftChip({ workstream }: { workstream: WorkstreamView }) {
  const drift = workstream.templateDriftPercent;
  if (drift === null || drift === undefined || drift === "0.00") return null;
  const over = Number(drift) > 0;
  return (
    <span
      className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
      title={`Template distributed ${workstream.templateHours ?? "—"} hours; the grid now holds ${workstream.enteredHours ?? workstream.totalHours} entered.`}
    >
      {over ? "+" : "−"}
      {Math.abs(Number(drift)).toFixed(2)}% vs template
    </span>
  );
}

function AddLineRow(props: {
  geo: Geo;
  plan: ResourcePlanDetailView;
  months: readonly string[];
  steadyState: boolean;
  workstreamId: string | null;
  pending: boolean;
  onAdd: (input: {
    laborCategoryId: string;
    clientLaborCategory: string | null;
    billRate?: string;
    rateOverrideReason?: string;
    steadyStateHours?: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const { geo, plan } = props;
  const { data: categories } = useLaborCategories();
  const [categoryId, setCategoryId] = useState("");
  const [rate, setRate] = useState("");
  const [reason, setReason] = useState("");
  const [runRate, setRunRate] = useState("");

  const clientDictated = plan.rateMode === "ClientDictated";
  const cardRate = categoryId === "" ? null : (plan.cardRates[categoryId] ?? null);

  // Picking a category prefills the default rate, so the planner sees what it will cost
  // before they decide whether to override it.
  const pickCategory = (id: string) => {
    setCategoryId(id);
    const next = plan.cardRates[id] ?? "";
    if (!clientDictated) setRate(next);
  };

  const overriding = !clientDictated && rate !== "" && cardRate !== null && rate !== cardRate;
  const canAdd =
    categoryId !== "" &&
    (!clientDictated || rate !== "") &&
    (!overriding || reason.trim() !== "") &&
    !props.pending;

  const submit = () => {
    if (!canAdd) return;
    props.onAdd({
      laborCategoryId: categoryId,
      // A Client Label is set from the Plan Line dialog, not here: it is an occasional
      // rename of an already-priced line, not part of adding one.
      clientLaborCategory: null,
      ...(rate === "" ? {} : { billRate: rate }),
      ...(overriding ? { rateOverrideReason: reason.trim() } : {}),
      ...(props.steadyState && runRate !== "" ? { steadyStateHours: runRate } : {}),
    });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      props.onCancel();
    }
  };

  return (
    <tr className="bg-primary/5">
      <td
        className="sticky left-0 z-10 border-b border-border bg-primary/5 px-2"
        style={{ width: geo.category.width, height: 38 }}
      >
        <Select value={categoryId} onValueChange={pickCategory}>
          <SelectTrigger className="h-7 text-sm" aria-label="Labor Category">
            <SelectValue placeholder="Labor Category…" />
          </SelectTrigger>
          <SelectContent>
            {(categories ?? [])
              .filter((c) => c.active)
              .map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                  {plan.cardRates[c.id] !== undefined && ` · ${plan.cardRates[c.id]}`}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </td>

      <td
        className="sticky z-10 border-b border-border bg-primary/5"
        style={{ left: geo.who.left, width: geo.who.width }}
      />

      <td
        className="sticky z-10 border-b border-border bg-primary/5 px-1"
        style={{ left: geo.rate?.left, width: geo.rate?.width }}
      >
        <div className="relative">
          <span className="pointer-events-none absolute left-1 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            $
          </span>
          <Input
            inputMode="decimal"
            value={rate}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setRate(v);
            }}
            onKeyDown={onKeyDown}
            placeholder={cardRate ?? "rate"}
            aria-label="Bill Rate"
            title={
              cardRate === null
                ? clientDictated
                  ? "This plan uses Client-Dictated rates, so every line carries its own."
                  : "No card rate for this Labor Category on this plan's Rate Basis."
                : `Card rate on ${plan.rateBasisLabel}: ${fmtMoney(cardRate)}`
            }
            className={cn(
              "h-7 pl-4 pr-1 text-right text-sm tabular-nums",
              overriding && "font-semibold text-primary",
            )}
          />
        </div>
      </td>

      {geo.runRate !== null && (
        <td
          className="sticky z-10 border-b border-border bg-primary/5 px-1"
          style={{ left: geo.runRate.left, width: geo.runRate.width }}
        >
          <Input
            inputMode="decimal"
            value={runRate}
            onChange={(e) => {
              const v = e.target.value;
              if (isTypableHours(v)) setRunRate(v);
            }}
            onKeyDown={onKeyDown}
            placeholder="hrs/mo"
            aria-label="Hours per month"
            title={`Applied to every one of the plan's ${plan.months.length} projected months.`}
            className="h-7 px-1 text-right text-sm tabular-nums"
          />
        </td>
      )}

      <td
        colSpan={geo.cost === null ? 2 : 4}
        className="sticky z-10 border-b border-r-2 border-border bg-primary/5 px-2"
        style={{ left: geo.hours.left, width: geo.frozenWidth - geo.hours.left }}
      >
        <div className="flex items-center justify-end gap-1">
          {overriding && (
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Override reason"
              aria-label="Override reason"
              title="An override needs a reason — it is captured where it happened."
              className="h-7 flex-1 text-xs"
            />
          )}
          <Button size="sm" className="h-7 px-2 text-sm" disabled={!canAdd} onClick={submit}>
            Add
          </Button>
          <button
            type="button"
            onClick={props.onCancel}
            aria-label="Cancel"
            className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </td>

      {props.months.length > 0 && (
        <td colSpan={props.months.length} className="border-b border-border bg-primary/5" />
      )}
    </tr>
  );
}

// ── One row ───────────────────────────────────────────────────────────────────

interface GridRowProps {
  geo: Geo;
  line: PlanLineView;
  /** Names the Account and Rate Basis behind each line's rate source. */
  plan: ResourcePlanDetailView;
  rowIndex: number;
  months: readonly string[];
  /** Bumped when THIS row changes. The memo key — see the header. */
  version: number;
  steadyState: boolean;
  totalHours: string;
  totalAmount: string;
  runRate: string;
  rect: { top: number; bottom: number; left: number; right: number };
  readCell: (lineId: string, month: string) => string;
  writeCell: (lineId: string, month: string, value: string) => CellPatch;
  writeRunRate: (lineId: string, value: string) => void;
  commit: (touched: Iterable<string>) => void;
  onCellKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, ref: CellRef) => void;
  onCellFocus: (ref: CellRef) => void;
  onCellMouseDown: (ref: CellRef, shift: boolean) => void;
  onHandleDown: (event: React.PointerEvent) => void;
  readOnly: boolean;
  /** An existing line's bill rate — Draft only, even when content is open after Won. */
  ratesReadOnly: boolean;
  /** Hours cells and the run-rate — also editable by an RM staffing a sent plan. */
  hoursReadOnly: boolean;
  /** The Who column and status chip — editable past Draft. */
  resourcesReadOnly: boolean;
  onOpenLine: (line: PlanLineView) => void;
  onOpenWho: (line: PlanLineView, anchor: HTMLElement) => void;
  onConfirmWho: (line: PlanLineView) => void;
  onSetLineStatus: PlanGridProps["onSetLineStatus"];
  onEditRate: (line: PlanLineView, anchor: HTMLElement) => void;
  /**
   * `undefined` = no eye, no bar for this row (no suggested Resource, not
   * viewable, or the overlay is still loading). Present means one entry per `months`.
   */
  rowLoad?: readonly MonthLoad[];
  /** The suggested Resource's in-force Account reservation; absent = unreserved. */
  reservation?: ResourcePickerReservationView;
  /** The suggested Resource's Fit Score for this line; absent = none to show. */
  fit?: ResourcePickerFitView;
  onViewResource?: (resourceId: string) => void;
  /** RP-24 — whether this line's subtree of Resources is open. */
  expanded: boolean;
  onToggleExpand: (lineId: string) => void;
  onOpenCandidates?: PlanGridProps["onOpenCandidates"];
  onOpenNotes?: PlanGridProps["onOpenNotes"];
}

/**
 * Memoized on `(line, version, …)`.
 *
 * This is the whole performance story: a keystroke bumps one row's version, so React
 * re-renders that row and the footer, not 1,440 inputs. The selection rectangle is
 * compared by value, and only when it actually intersects this row.
 */
const GridRow = memo(
  function GridRow(props: GridRowProps) {
    const { geo, line, rowIndex, months } = props;
    const isOverridden = line.rateSource === "LineOverride";
    const isClientDictated = line.rateSource === "ClientDictated";
    // RP-6.1: an Account-negotiated rate (RP-12.4) applied to this line — every applied
    // rate must be labeled, so this gets the same dot + label treatment as the others.
    const isAccountOverride = line.rateSource === "AccountOverride";
    const rateSource = describeRateSource(line, props.plan);

    return (
      <tr className="group">
        <td
          className="sticky left-0 z-10 border-b border-border bg-card px-3"
          style={{ width: geo.category.width, height: 34 }}
        >
          {/*
           * The Client Label REPLACES the Labor Category here rather than sitting in a
           * column of its own. A label is the client-facing name for the same priced
           * thing, so showing both side by side spent 148px saying one idea twice — and
           * on a client-facing read the label is the name that matters. The Labor
           * Category is still the priced identity, so it stays in the tooltip, and the
           * Discipline code beside it never moves.
           */}
          <span className="flex w-full items-center gap-1.5">
            {/*
             * RP-24 — every row expands into a subtree of the Resources on it, where the RM
             * splits the line. A split line's chevron is primary-coloured so the split is
             * visible while collapsed.
             */}
            <button
              type="button"
              onClick={() => props.onToggleExpand(line.id)}
              aria-expanded={props.expanded}
              aria-label={`${props.expanded ? "Collapse" : "Expand"} the Resources on ${line.laborCategoryName}`}
              title={
                isSplitLine(line)
                  ? `Split across ${line.shares?.length ?? 0} Resources — expand to see who covers which hours`
                  : "Expand to see who is on this line, or split it across Resources"
              }
              className={cn(
                "-ml-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-muted",
                isSplitLine(line) ? "text-primary" : "text-muted-foreground",
              )}
            >
              {props.expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => props.onOpenLine(line)}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
              title={
                line.clientLaborCategory === null
                  ? "Edit this Plan Line"
                  : `Client label for ${line.laborCategoryName}. Click to edit this Plan Line.`
              }
            >
              <span className="truncate text-sm font-medium">
                {line.clientLaborCategory ?? line.laborCategoryName}
              </span>
              {line.clientLaborCategory !== null && (
                // Decorative: the button's own title already names the Labor Category
                // underneath, so announcing this twice would only add noise.
                <span className="shrink-0 text-xs text-muted-foreground/70" aria-hidden>
                  ✎
                </span>
              )}
              {line.disciplineCode !== null && (
                <span
                  className="shrink-0 text-xs font-semibold text-muted-foreground"
                  title={`Pod-eligible — maps to the ${line.disciplineCode} Discipline`}
                >
                  {line.disciplineCode}
                </span>
              )}
            </button>
            <LineStatusChip
              line={line}
              // A split line's status follows its Shares (RP-24); it has nothing to set.
              readOnly={props.resourcesReadOnly || isSplitLine(line)}
              onSet={props.onSetLineStatus}
            />
            {props.reservation !== undefined && (
              // The Who column is full with the name and Fit Score, so the reservation
              // shows here — the nearest wide cell — as the shared badge's lock alone.
              // The badge keeps its full sentence as the hover title and the label as
              // screen-reader text, so nothing is lost but the pixels.
              <ReservationBadge
                accountName={props.reservation.accountName}
                until={props.reservation.until}
                forThisAccount={props.reservation.forThisAccount}
                className="shrink-0 px-1 [&>span]:sr-only"
              />
            )}
          </span>
        </td>

        <WhoCell
          slot={geo.who}
          line={line}
          readOnly={props.resourcesReadOnly}
          onOpen={props.onOpenWho}
          onConfirm={props.onConfirmWho}
          canViewProfile={props.rowLoad !== undefined}
          onViewProfile={props.onViewResource}
          fit={props.fit}
          onToggleExpand={props.onToggleExpand}
          onOpenCandidates={props.onOpenCandidates}
          onOpenNotes={props.onOpenNotes}
        />

        {geo.rate !== null && (
          <td
            className="sticky z-10 border-b border-border bg-card px-2 text-right"
            style={{ left: geo.rate.left, width: geo.rate.width }}
          >
            {/*
             * On a Draft the figure itself is the edit target — the planner prices a line
             * where they read it, not through the Plan Line dialog. Read-only plans keep
             * the plain figure: nothing to click means nothing that looks clickable.
             */}
            <RateFigure line={line} readOnly={props.ratesReadOnly} onEdit={props.onEditRate}>
              {/*
               * The source is carried by colour alone, not a word ("Say it once"): violet
               * is a custom rate on this line, blue is the client's own rate (Client-set
               * or the Account's negotiated card), ink is the list Rate Card. The full
               * sentence stays on hover, and the column header carries the key.
               */}
              <span
                className={cn(
                  "cursor-help text-sm tabular-nums",
                  isOverridden && "font-semibold text-primary",
                  (isClientDictated || isAccountOverride) &&
                    "font-semibold text-info-700 dark:text-info-400",
                  !rateSource.departsFromStandard && "text-foreground",
                )}
                title={rateSource.detail}
              >
                {fmtMoney(line.billRate)}
              </span>
            </RateFigure>
          </td>
        )}

        {geo.runRate !== null && (
          <td
            className="sticky z-10 border-b border-border bg-card px-1"
            style={{ left: geo.runRate.left, width: geo.runRate.width }}
          >
            <input
              // The run-rate: ONE number that applies to every projected month (RP-4.4).
              inputMode="decimal"
              value={props.runRate}
              readOnly={props.hoursReadOnly}
              placeholder={props.hoursReadOnly ? undefined : "0"}
              aria-label={`Hours per month, ${line.laborCategoryName}`}
              title={props.hoursReadOnly ? undefined : "Type the hours per month for this line."}
              onChange={(event) => {
                if (props.hoursReadOnly) return;
                const next = event.target.value;
                if (!isTypableHours(next)) return;
                props.writeRunRate(line.id, next);
              }}
              className={cn(
                "font-medium",
                props.hoursReadOnly
                  ? "h-7 w-full cursor-default bg-transparent px-1 text-right text-sm tabular-nums outline-none"
                  : RUN_RATE_INPUT,
              )}
            />
          </td>
        )}

        <td
          className={cn(
            "sticky z-10 border-b border-border bg-card px-2 text-right text-sm font-medium tabular-nums",
            geo.amount === null && "border-r-2",
          )}
          style={{ left: geo.hours.left, width: geo.hours.width }}
        >
          {fmtNum(props.totalHours)}
        </td>

        {geo.amount !== null && (
          <td
            className={cn(
              "sticky z-10 border-b border-border bg-card px-2.5 text-right text-sm text-muted-foreground tabular-nums",
              geo.margin === null && "border-r-2",
            )}
            style={{ left: geo.amount.left, width: geo.amount.width }}
          >
            {fmtMoney(props.totalAmount)}
          </td>
        )}

        {geo.cost !== null && (
          <td
            className="sticky z-10 border-b border-border bg-card px-2.5 text-right text-sm text-muted-foreground tabular-nums"
            style={{ left: geo.cost.left, width: geo.cost.width }}
            title={costTitle(line)}
          >
            {line.totalCost === undefined ? (
              <InternalRateChip />
            ) : (
              <span className="inline-flex items-center justify-end gap-1">
                {line.internalRateSource !== undefined &&
                  line.internalRateSource !== "labor_category" && (
                    <UserLock className="h-3 w-3 shrink-0 text-primary" aria-hidden />
                  )}
                {fmtMoney(line.totalCost)}
              </span>
            )}
          </td>
        )}

        {geo.margin !== null && (
          <td
            className="sticky z-10 border-b border-r-2 border-border bg-card px-2.5 text-right text-sm tabular-nums"
            style={{ left: geo.margin.left, width: geo.margin.width }}
          >
            <MarginFigure percent={line.marginPercent} plan={props.plan} />
          </td>
        )}

        {months.map((month, col) => {
          const selected = !props.steadyState && rectContains(props.rect, rowIndex, col);
          const isCorner =
            !props.steadyState && props.rect.bottom === rowIndex && props.rect.right === col;
          const load = props.rowLoad?.[col];
          return (
            <Cell
              key={month}
              lineId={line.id}
              month={month}
              row={rowIndex}
              col={col}
              selected={selected}
              showHandle={isCorner && !props.hoursReadOnly}
              // In steady-state the months are a PROJECTION of the run-rate, so they are
              // read-only: two places to type the same number is how they drift.
              readOnly={props.hoursReadOnly || props.steadyState}
              projected={props.steadyState}
              value={props.steadyState ? props.runRate : props.readCell(line.id, month)}
              writeCell={props.writeCell}
              commit={props.commit}
              onKeyDown={props.onCellKeyDown}
              onFocus={props.onCellFocus}
              onMouseDown={props.onCellMouseDown}
              onHandleDown={props.onHandleDown}
              loadTier={load?.tier ?? null}
              loadTooltip={load?.tooltip ?? null}
            />
          );
        })}
      </tr>
    );
  },
  (prev, next) =>
    prev.line === next.line &&
    // Compared by the fields the rate tag reads — the plan object changes identity often.
    prev.plan.rateBasisLabel === next.plan.rateBasisLabel &&
    prev.plan.clientAccountName === next.plan.clientAccountName &&
    // ...and the two the Margin cell colors against.
    prev.plan.marginFloorPercent === next.plan.marginFloorPercent &&
    prev.plan.marginAmberBandPoints === next.plan.marginAmberBandPoints &&
    prev.version === next.version &&
    prev.totalHours === next.totalHours &&
    prev.totalAmount === next.totalAmount &&
    prev.runRate === next.runRate &&
    prev.readOnly === next.readOnly &&
    prev.ratesReadOnly === next.ratesReadOnly &&
    prev.hoursReadOnly === next.hoursReadOnly &&
    prev.resourcesReadOnly === next.resourcesReadOnly &&
    prev.steadyState === next.steadyState &&
    prev.months === next.months &&
    prev.rowIndex === next.rowIndex &&
    prev.geo === next.geo &&
    sameRowLoad(prev.rowLoad, next.rowLoad) &&
    sameReservation(prev.reservation, next.reservation) &&
    // One query result per fetch, so identity changes exactly when any fit field does.
    prev.fit === next.fit &&
    prev.expanded === next.expanded &&
    // The selection rectangle only matters to this row when it intersects it.
    rowIntersects(prev.rect, prev.rowIndex) === rowIntersects(next.rect, next.rowIndex) &&
    (!rowIntersects(next.rect, next.rowIndex) ||
      (prev.rect.left === next.rect.left &&
        prev.rect.right === next.rect.right &&
        prev.rect.top === next.rect.top &&
        prev.rect.bottom === next.rect.bottom)),
);

/** Value-equality for a row's reservation — see `sameRowLoad` for why a reference check will not do. */
function sameReservation(
  a: ResourcePickerReservationView | undefined,
  b: ResourcePickerReservationView | undefined,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return (
    a.forThisAccount === b.forThisAccount && a.accountName === b.accountName && a.until === b.until
  );
}

function rowIntersects(rect: { top: number; bottom: number }, row: number): boolean {
  return row >= rect.top && row <= rect.bottom;
}

/**
 * Cheap value-equality for a row's load-bar slice. `rowLoadByLineId` is rebuilt as a
 * whole every time the guidance query resolves or a keystroke bumps `deferredVersion`,
 * so a reference check alone would defeat the GridRow memo for every row, every time —
 * this is what lets an UNCHANGED row's bars stay put while a typed row's bars update.
 */
function sameRowLoad(
  a: readonly MonthLoad[] | undefined,
  b: readonly MonthLoad[] | undefined,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x === undefined || y === undefined) return x === y;
    if (x.tier !== y.tier || x.tooltip !== y.tooltip) return false;
  }
  return true;
}

/**
 * The Rate cell's content, made clickable on an editable plan. A button, not an input:
 * a rate change can need an override reason, which an in-cell input has no room for, so
 * the click opens the inline rate editor anchored to this cell instead.
 */
function RateFigure(props: {
  line: PlanLineView;
  readOnly: boolean;
  onEdit: (line: PlanLineView, anchor: HTMLElement) => void;
  children: React.ReactNode;
}) {
  if (props.readOnly) return <>{props.children}</>;
  return (
    <button
      type="button"
      onClick={(event) => props.onEdit(props.line, event.currentTarget)}
      title="Edit this line's bill rate"
      aria-label={`Edit the bill rate for ${props.line.clientLaborCategory ?? props.line.laborCategoryName}`}
      className="-mx-1 inline-flex rounded px-1 py-0.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {props.children}
    </button>
  );
}

const LINE_STATUS_CLASS: Record<PlanLineStatus, string> = {
  Open: "border border-dashed border-warning-500/60 bg-warning-100 text-warning-700 dark:text-warning-400",
  Proposed: "bg-info-100 text-info-700",
  Confirmed: "bg-success-100 text-success-700",
};

const LINE_STATUS_HINT: Record<PlanLineStatus, string> = {
  Open: "Nobody named yet",
  Proposed: "Named, not yet confirmed",
  Confirmed: "Confirmed as who the plan proposes",
};

function lineStatusChipClass(status: PlanLineStatus): string {
  return cn(
    "inline-flex h-5 shrink-0 items-center gap-0.5 rounded-full px-1.5 text-xs font-medium",
    LINE_STATUS_CLASS[status],
  );
}

/**
 * A line's resourcing status as a plain chip, nothing to click. The read-only grid shows
 * it, and so does the shared Plan Overview's grid (SharedPlanGrid.tsx) — one definition,
 * so the three colors cannot drift between the working view and the shared one.
 */
export function LineStatusBadge({ status }: { status: PlanLineStatus }) {
  return (
    <span className={lineStatusChipClass(status)} title={LINE_STATUS_HINT[status]}>
      {status}
    </span>
  );
}

/**
 * The Labor Category's resourcing status — Open, Proposed or Confirmed — and the menu that
 * changes it. Derived from the Who column's two facts (see src/lib/plan-line-status.ts), so
 * the chip and the Who chip can never disagree.
 *
 * An Open line's chip opens the Resource picker directly: Proposed and Confirmed are both
 * statements about a person, so "pick someone" is the only useful next step and a menu in
 * front of it would be a wasted click. Picking someone lands the line on Proposed.
 */
function LineStatusChip(props: {
  line: PlanLineView;
  readOnly: boolean;
  onSet: PlanGridProps["onSetLineStatus"];
}) {
  const { line } = props;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const status = planLineStatus(line);
  const chipClass = lineStatusChipClass(status);

  if (props.readOnly) return <LineStatusBadge status={status} />;

  if (status === "Open") {
    return (
      <button
        type="button"
        className={cn(chipClass, "transition-opacity hover:opacity-80")}
        title="Open — nobody named yet. Click to assign a Resource; they start as Proposed."
        aria-label={`${line.laborCategoryName} is Open. Assign a Resource`}
        onClick={(event) => props.onSet(line, "Proposed", event.currentTarget)}
      >
        Open
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(chipClass, "transition-opacity hover:opacity-80")}
          title={`${status} — ${line.suggestedResourceName}. Click to change.`}
          aria-label={`${line.laborCategoryName} is ${status}. Change status`}
        >
          {status}
          <ChevronDown className="h-2.5 w-2.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[13rem]">
        {PLAN_LINE_STATUSES.map((to) => (
          <DropdownMenuItem
            key={to}
            disabled={to === status}
            onSelect={() => {
              // Anchored to the chip, not the menu item: the item unmounts as the menu
              // closes, and anything opened beside it would float off a detached node.
              const anchor = triggerRef.current;
              if (anchor !== null) props.onSet(line, to, anchor);
            }}
          >
            <span className={cn("rounded-full px-1.5 text-xs font-medium", LINE_STATUS_CLASS[to])}>
              {to}
            </span>
            <span className="text-xs text-muted-foreground">
              {to === "Open" ? "Clear the Resource" : LINE_STATUS_HINT[to]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Cost & Margin (ADMIN / RM / FIN) ─────────────────────────────────────────

const SHOW_MONEY_KEY = "plan-grid:show-cost-margin";

/** Per-viewer convenience only — a blocked or empty store just means "shown". */
function readShowMoneyPref(): boolean {
  try {
    return localStorage.getItem(SHOW_MONEY_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeShowMoneyPref(show: boolean): void {
  try {
    localStorage.setItem(SHOW_MONEY_KEY, String(show));
  } catch {
    // Storage unavailable: the toggle still works for this visit.
  }
}

// ── Monthly summary rows (Revenue / Margin / Cost / FTE per month) ───────────

const SHOW_SUMMARY_KEY = "plan-grid:show-month-summary";

/** Per-viewer convenience only — a blocked or empty store just means "shown". */
function readShowSummaryPref(): boolean {
  try {
    return localStorage.getItem(SHOW_SUMMARY_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeShowSummaryPref(show: boolean): void {
  try {
    localStorage.setItem(SHOW_SUMMARY_KEY, String(show));
  } catch {
    // Storage unavailable: the toggle still works for this visit.
  }
}

/**
 * The grid's view options, in the frozen corner so they stay in reach while scrolling.
 * Display only, bar one: "Add Workstream…" sits here too, because grouping lines is a
 * less-than-once-a-visit action and a standing row under the table was a band the grid
 * could use ("Say it once"). Cost & margin is offered only to the roles that are sent
 * cost (Admin, Finance, Resource Managers).
 */
function GridViewMenu(props: {
  canViewCost: boolean;
  showMoney: boolean;
  onShowMoney: (show: boolean) => void;
  showSummary: boolean;
  onShowSummary: (show: boolean) => void;
  readOnly: boolean;
  onAddWorkstream: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label="Grid view options"
          title="Grid view options"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[15rem]">
        {props.canViewCost && (
          <DropdownMenuCheckboxItem
            checked={props.showMoney}
            onCheckedChange={(checked) => props.onShowMoney(checked === true)}
            title="Cost and Margin per Plan Line — visible to Admin, Finance and Resource Managers only"
          >
            Show cost &amp; margin
          </DropdownMenuCheckboxItem>
        )}
        <DropdownMenuCheckboxItem
          checked={props.showSummary}
          onCheckedChange={(checked) => props.onShowSummary(checked === true)}
          title="The per-month rows under Total: revenue, margin, cost and FTE"
        >
          Show monthly summary
        </DropdownMenuCheckboxItem>
        {!props.readOnly && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={props.onAddWorkstream}
              title="Workstreams are optional — a way to group Plan Lines into sections the client recognizes."
            >
              <Plus className="h-3.5 w-3.5" />
              Add Workstream…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function costTitle(line: PlanLineView): string {
  const rate = line.costRate === undefined ? "" : ` at ${fmtMoney(line.costRate)}/h`;
  switch (line.internalRateSource) {
    case "resource":
      return `Cost${rate} — ${line.suggestedResourceName ?? "the Resource"}'s own Internal Rate`;
    case "mixed":
      return `Cost${rate} (hour-weighted) — partly ${line.suggestedResourceName ?? "the Resource"}'s Internal Rate, partly the Labor Category average`;
    default:
      return `Cost${rate} — ${line.laborCategoryName}'s average internal cost`;
  }
}

/**
 * A margin %, colored against the plan's own floor and warning band — the same three
 * states as the header's margin badge, so a red line and a red plan mean the same thing.
 */
function MarginFigure(props: {
  percent: string | null;
  plan: Pick<ResourcePlanDetailView, "marginFloorPercent" | "marginAmberBandPoints">;
}) {
  if (props.percent === null) return <span className="text-muted-foreground">—</span>;
  const value = Number(props.percent);
  const floor = Number(props.plan.marginFloorPercent);
  const band = Number(props.plan.marginAmberBandPoints);
  const tone =
    value < floor
      ? "text-error-700 dark:text-error-400"
      : value < floor + band
        ? "text-warning-700 dark:text-warning-400"
        : "text-success-700 dark:text-success-400";
  return (
    <span
      className={cn("font-semibold", tone)}
      title={`Floor ${props.plan.marginFloorPercent}% · warning band ${props.plan.marginAmberBandPoints} points`}
    >
      {value.toFixed(1)}%
    </span>
  );
}

/**
 * The Who column — avatar, full name and the Resource's Fit Score for this line.
 *
 * Avatar: solid brand = Confirmed · tinted with a dashed ring = Proposed · dashed gray =
 * Open. It used to be a 72px color-only chip; planners hovered every row to learn who was
 * on it, so the name and the score now earn the width. The Position is STILL written to
 * the Plan Line (Handover pre-fills Pod Positions from it) — it just does not earn a badge.
 *
 * The Fit Score is the picker's own per-candidate score, asked against this line's
 * Discipline. It is absent for an actor who may not see ratings — the server never sends
 * it — and the cell then shows the name alone.
 *
 * Confirming records who the plan PROPOSES. It does not put anyone on the Pod.
 *
 * The eye icon is its own button beside the name: the name's click target is reserved for
 * the Suggested Resource picker, so "view their profile" cannot share it.
 */
function WhoCell(props: {
  slot: Slot;
  line: PlanLineView;
  readOnly: boolean;
  onOpen: (line: PlanLineView, anchor: HTMLElement) => void;
  onConfirm: (line: PlanLineView) => void;
  /** True once `usePlanGuidanceLoad` confirms this row's suggested Resource is viewable. */
  canViewProfile: boolean;
  onViewProfile?: (resourceId: string) => void;
  fit?: ResourcePickerFitView;
  /** RP-24 — a split line's Who cell expands its subtree instead of opening the picker. */
  onToggleExpand?: (lineId: string) => void;
  onOpenCandidates?: (line: PlanLineView, anchor: HTMLElement) => void;
  onOpenNotes?: (line: PlanLineView) => void;
}) {
  const { line, fit } = props;
  if (isSplitLine(line)) return <SplitWhoCell {...props} />;
  const resourceId = line.suggestedResourceId;
  const name = line.suggestedResourceName ?? "";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  const state =
    resourceId === null ? "empty" : line.suggestedResourceConfirmed ? "confirmed" : "suggested";

  const title =
    state === "empty"
      ? "Nobody suggested yet — click to note who the client has met or who we pitched"
      : state === "confirmed"
        ? `${name} — confirmed as the plan's proposal. They go onto the Solution's Pod when the plan is set to Staffed.`
        : `${name} — proposed. Click the tick to confirm; confirming records who the plan proposes and books no capacity.`;

  const showEye = props.canViewProfile && resourceId !== null && props.onViewProfile !== undefined;
  // RP-32.1. The reason rides on the name's tooltip; Client Requested also gets a chip,
  // because changing that Resource is a client conversation, not a staffing call.
  const reason =
    state === "empty"
      ? null
      : describeSuggestedResourceReason(
          line.suggestedResourceReason,
          line.suggestedResourceReasonNote,
        );
  const clientRequested = state !== "empty" && line.suggestedResourceReason === "ClientRequested";

  return (
    <td
      className="sticky z-10 border-b border-border bg-card px-2"
      style={{ left: props.slot.left, width: props.slot.width }}
    >
      <span className="flex w-full min-w-0 items-center gap-1.5">
        <button
          type="button"
          onClick={(event) => {
            if (props.readOnly) return;
            props.onOpen(line, event.currentTarget);
          }}
          title={reason === null ? title : `${title} Reason: ${reason}.`}
          aria-label={title}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-1.5 rounded-md text-left",
            props.readOnly ? "cursor-default" : "group/who",
          )}
        >
          <span
            className={cn(
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors",
              state === "confirmed" && "bg-primary text-primary-foreground",
              state === "suggested" &&
                "border border-dashed border-primary bg-primary/15 text-primary",
              state === "empty" &&
                "border border-dashed border-muted-foreground/40 text-muted-foreground/50",
              state === "empty" &&
                !props.readOnly &&
                "group-hover/who:border-primary group-hover/who:text-primary",
            )}
          >
            {state === "empty" ? "+" : initials}
          </span>
          <span
            className={cn(
              "truncate text-sm",
              state === "empty"
                ? "text-muted-foreground"
                : "font-medium text-foreground group-hover/who:text-primary",
            )}
          >
            {state === "empty" ? (props.readOnly ? "Open" : "Add Resource") : name}
          </span>
        </button>
        {clientRequested && (
          <span
            className="shrink-0 rounded bg-warning-100 px-1 text-xs font-semibold text-warning-950"
            title={`Client Requested${reason !== null && reason !== "Client Requested" ? ` — ${reason}` : ""}. Changing this Resource is a conversation with the client.`}
          >
            Client
          </span>
        )}
        {fit !== undefined && state !== "empty" && (
          <FitBadge
            fit={{ tier: fit.tier, score: Math.round(Number(fit.score)), partial: fit.partial }}
            size="xs"
            hideTier
            title={`${fit.tier} — ${fitExplanation(fit)}`}
          />
        )}
        {state === "suggested" && !props.readOnly && (
          <button
            type="button"
            onClick={() => props.onConfirm(line)}
            title="Confirm — records who the plan proposes, and books no capacity"
            aria-label={`Confirm ${name}`}
            className="shrink-0 text-xs font-bold text-primary"
          >
            ✓
          </button>
        )}
        <LineShortlistButtons
          line={line}
          onOpenCandidates={props.onOpenCandidates}
          onOpenNotes={props.onOpenNotes}
        />
        {showEye && resourceId !== null && props.onViewProfile !== undefined && (
          <button
            type="button"
            onClick={() => props.onViewProfile?.(resourceId)}
            title={`View ${name}'s profile`}
            aria-label={`View ${name}'s profile`}
            className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </td>
  );
}

// ── RP-24 Line Shares — the row subtree ──────────────────────────────────────

/**
 * A split line's Who cell: every Share holder as first name and last initial,
 * slash-separated ("Ada L / Bo M"), full names on hover (Martyn, 2026-10-17). Clicking it
 * opens the line's subtree, where the split is edited. A dot flags hours the Shares leave
 * unallocated (amber) or exceed (red).
 */
/**
 * RP-32 — the Candidates and Notes buttons on a line's Who cell. Shared by the ordinary Who
 * cell and RP-24's split-line cell, so a split line keeps its shortlist and notes.
 */
function LineShortlistButtons(props: {
  line: PlanLineView;
  onOpenCandidates?: (line: PlanLineView, anchor: HTMLElement) => void;
  onOpenNotes?: (line: PlanLineView) => void;
}) {
  const { line } = props;
  const candidateCount = line.activeCandidateCount ?? 0;
  const noteCount = line.noteCount ?? 0;
  return (
    <>
      {props.onOpenCandidates !== undefined && (
        <button
          type="button"
          onClick={(event) => props.onOpenCandidates?.(line, event.currentTarget)}
          title={
            candidateCount === 0
              ? "Candidates — the RM's shortlist for this line. Books no capacity."
              : `${candidateCount} Candidate${candidateCount === 1 ? "" : "s"} shortlisted — books no capacity`
          }
          aria-label={`Candidates for ${line.laborCategoryName}`}
          className={cn(
            "inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-xs transition-colors hover:text-primary",
            candidateCount > 0 ? "font-semibold text-primary" : "text-muted-foreground/60",
          )}
        >
          <Users className="h-3.5 w-3.5" />
          {candidateCount > 0 && candidateCount}
        </button>
      )}
      {props.onOpenNotes !== undefined && (
        <button
          type="button"
          onClick={() => props.onOpenNotes?.(line)}
          title={
            noteCount === 0
              ? "Notes on this line — internal only, never in the client export"
              : `${noteCount} note${noteCount === 1 ? "" : "s"} — internal only`
          }
          aria-label={`Notes for ${line.laborCategoryName}`}
          className={cn(
            "inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-xs transition-colors hover:text-primary",
            noteCount > 0 ? "font-semibold text-primary" : "text-muted-foreground/60",
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {noteCount > 0 && noteCount}
        </button>
      )}
    </>
  );
}

function SplitWhoCell(props: {
  slot: Slot;
  line: PlanLineView;
  onToggleExpand?: (lineId: string) => void;
  onOpenCandidates?: (line: PlanLineView, anchor: HTMLElement) => void;
  onOpenNotes?: (line: PlanLineView) => void;
}) {
  const { line } = props;
  const names = (line.shares ?? []).map((share) => share.resourceName);
  const over = Number(line.shareBalance?.overHours ?? "0") > 0;
  const under = Number(line.shareBalance?.unallocatedHours ?? "0") > 0;
  const balance = over
    ? ` — ${fmtNum(line.shareBalance?.overHours ?? "0")} hrs over-allocated`
    : under
      ? ` — ${fmtNum(line.shareBalance?.unallocatedHours ?? "0")} hrs unallocated`
      : "";
  const title = `Split across ${names.join(", ")}${balance}. Expand the row to edit who covers which hours.`;
  return (
    <td
      className="sticky z-10 border-b border-border bg-card px-2"
      style={{ left: props.slot.left, width: props.slot.width }}
    >
      <span className="flex w-full min-w-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => props.onToggleExpand?.(line.id)}
          title={title}
          aria-label={title}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md text-left"
        >
          <span className="truncate text-sm font-medium text-foreground hover:text-primary">
            {formatShareHolders(names)}
          </span>
          {(over || under) && (
            <span
              aria-hidden
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                over ? "bg-destructive" : "bg-[var(--fit-moderate)]",
              )}
            />
          )}
        </button>
        <LineShortlistButtons
          line={line}
          onOpenCandidates={props.onOpenCandidates}
          onOpenNotes={props.onOpenNotes}
        />
      </span>
    </td>
  );
}

interface LineSubtreeProps {
  geo: Geo;
  line: PlanLineView;
  months: readonly string[];
  steadyState: boolean;
  shareRows: readonly GridRowModel[];
  rowVersion: Record<string, number>;
  rect: { top: number; bottom: number; left: number; right: number };
  readCell: (lineId: string, month: string) => string;
  readRunRate: (lineId: string) => string;
  writeCell: (lineId: string, month: string, value: string) => CellPatch;
  writeRunRate: (lineId: string, value: string) => void;
  commit: (touched: Iterable<string>) => void;
  onCellKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, ref: CellRef) => void;
  onCellFocus: (ref: CellRef) => void;
  onCellMouseDown: (ref: CellRef, shift: boolean) => void;
  onHandleDown: (event: React.PointerEvent) => void;
  canManageShares: boolean;
  maxShares: number;
  readPoints: (rowKey: string) => string;
  writePoints: (rowKey: string, value: string) => void;
  onAddShareResource?: PlanGridProps["onAddShareResource"];
  onOpenWho: (line: PlanLineView, anchor: HTMLElement) => void;
  onRemoveShare: (line: PlanLineView, resourceId: string) => void;
  rowLoadByKey: Record<string, readonly MonthLoad[]>;
}

/**
 * The Resources on one line, beneath it (Martyn, 2026-10-17): "a very similar experience
 * to adding hours … just a subtree against the row I expand."
 *
 *   - A Primary Position line (R1–R4) is one person and never splits; its subtree says so.
 *   - A split line shows one row per Share — hours typed into the same kind of month cells
 *     as the line, in the same selection, fill and paste — then the per-month balance.
 *   - An unsplit line shows its one Resource, mirroring the line's hours, and lets the RM
 *     add a second, which is what splits it.
 */
function LineSubtree(props: LineSubtreeProps) {
  const { line, geo, months } = props;
  const split = isSplitLine(line);
  const shareCount = line.shares?.length ?? 0;

  if (isPrimaryPosition(line.position)) {
    return (
      <SubtreeNoteRow geo={geo} months={months}>
        {PRIMARY_SPLIT_REFUSAL}
      </SubtreeNoteRow>
    );
  }

  const canAdd = props.canManageShares && (split ? shareCount < props.maxShares : true);
  return (
    <>
      {split
        ? props.shareRows.map((row) => <ShareRow key={row.rowKey} row={row} {...props} />)
        : line.suggestedResourceId !== null && <SoleResourceRow {...props} />}
      {split && <UnallocatedRow {...props} />}
      {canAdd && (
        <tr>
          <td
            colSpan={geo.frozenCols}
            className="sticky left-0 z-10 border-b border-r-2 border-border bg-card py-1 pr-3 pl-9"
            style={{ width: geo.frozenWidth }}
          >
            <button
              type="button"
              onClick={(event) => {
                // A line naming nobody takes its first Resource as its Suggested Resource;
                // only a second one splits it.
                if (!split && line.suggestedResourceId === null) {
                  props.onOpenWho(line, event.currentTarget);
                  return;
                }
                props.onAddShareResource?.(line, event.currentTarget);
              }}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              title={
                !split && line.suggestedResourceId !== null
                  ? "Split this line: add a Resource to share its hours"
                  : "Add a Resource to this line"
              }
            >
              <Plus className="h-3 w-3" />
              Add Resource
            </button>
          </td>
          {months.length > 0 && <td colSpan={months.length} className="border-b border-border" />}
        </tr>
      )}
      {props.canManageShares && split && shareCount >= props.maxShares && (
        <SubtreeNoteRow geo={geo} months={months}>
          A line can be split across at most {props.maxShares} Resources.
        </SubtreeNoteRow>
      )}
      {!props.canManageShares && !split && line.suggestedResourceId === null && (
        <SubtreeNoteRow geo={geo} months={months}>
          Nobody on this line yet.
        </SubtreeNoteRow>
      )}
    </>
  );
}

function SubtreeNoteRow(props: { geo: Geo; months: readonly string[]; children: React.ReactNode }) {
  return (
    <tr>
      <td
        colSpan={props.geo.frozenCols}
        className="sticky left-0 z-10 border-b border-r-2 border-border bg-card py-1.5 pr-3 pl-9 text-xs text-muted-foreground"
        style={{ width: props.geo.frozenWidth }}
      >
        {props.children}
      </td>
      {props.months.length > 0 && (
        <td colSpan={props.months.length} className="border-b border-border" />
      )}
    </tr>
  );
}

/** "Ada Lovelace" → "AL", for the avatar disc. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** The cells after Who on a subtree row: Rate (blank), then Hours … Margin. */
function SubtreeFigureCells(props: {
  geo: Geo;
  runRate?: React.ReactNode;
  hours: React.ReactNode;
  tone?: string;
}) {
  const { geo } = props;
  return (
    <>
      {geo.rate !== null && (
        <td
          className="sticky z-10 border-b border-border bg-card"
          style={{ left: geo.rate.left, width: geo.rate.width }}
        />
      )}
      {geo.runRate !== null && (
        <td
          className="sticky z-10 border-b border-border bg-card px-1"
          style={{ left: geo.runRate.left, width: geo.runRate.width }}
        >
          {props.runRate}
        </td>
      )}
      <td
        className={cn(
          "sticky z-10 border-b border-border bg-card px-2 text-right text-sm tabular-nums",
          props.tone ?? "text-muted-foreground",
          geo.amount === null && "border-r-2",
        )}
        style={{ left: geo.hours.left, width: geo.hours.width }}
      >
        {props.hours}
      </td>
      {geo.amount !== null && (
        <td
          className={cn(
            "sticky z-10 border-b border-border bg-card",
            geo.margin === null && "border-r-2",
          )}
          style={{ left: geo.amount.left, width: geo.amount.width }}
        />
      )}
      {geo.cost !== null && (
        <td
          className="sticky z-10 border-b border-border bg-card"
          style={{ left: geo.cost.left, width: geo.cost.width }}
        />
      )}
      {geo.margin !== null && (
        <td
          className="sticky z-10 border-b border-r-2 border-border bg-card"
          style={{ left: geo.margin.left, width: geo.margin.width }}
        />
      )}
    </>
  );
}

/** The left two cells of a subtree row: the indented connector, then the Resource. */
function SubtreeLead(props: {
  geo: Geo;
  name: string;
  /** Points input (ADMIN / RM) — absent for everyone else. */
  points?: React.ReactNode;
  onRemove?: () => void;
}) {
  const { geo } = props;
  return (
    <>
      <td
        className="sticky left-0 z-10 border-b border-border bg-card pl-9 pr-2"
        style={{ width: geo.category.width, height: 30 }}
      >
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span aria-hidden className="h-3 w-3 shrink-0 border-b border-l border-border" />
          <span className="truncate">Share of this line</span>
          {props.onRemove !== undefined && (
            <button
              type="button"
              onClick={props.onRemove}
              aria-label={`Remove ${props.name} from this line`}
              title={`Remove ${props.name} from this line`}
              className="ml-auto inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      </td>
      <td
        className="sticky z-10 border-b border-border bg-card px-2"
        style={{ left: geo.who.left, width: geo.who.width }}
      >
        <span className="flex w-full min-w-0 items-center gap-1.5">
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
            {initialsOf(props.name)}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm" title={props.name}>
            {props.name}
          </span>
          {props.points}
        </span>
      </td>
    </>
  );
}

/** One Share holder's row: their hours per month, in the grid's own cells. */
function ShareRow(props: LineSubtreeProps & { row: GridRowModel }) {
  const { geo, row, months, steadyState } = props;
  const share = row.share;
  if (share === undefined) return null;
  const rowKey = row.rowKey;
  const selectable = row.rowIndex >= 0;
  const editable = props.canManageShares;
  void props.rowVersion[rowKey];

  const runRate = props.readRunRate(rowKey);
  const total = steadyState
    ? fromCenti(runRate === "" ? 0 : toMinor(runRate) * months.length)
    : fromCenti(
        months.reduce((sum, month) => {
          const raw = props.readCell(rowKey, month);
          return raw === "" ? sum : sum + toMinor(raw);
        }, 0),
      );
  const load = props.rowLoadByKey[rowKey];

  return (
    <tr className="group">
      <SubtreeLead
        geo={geo}
        name={share.resourceName}
        onRemove={editable ? () => props.onRemoveShare(props.line, share.resourceId) : undefined}
        points={
          editable ? (
            <label
              className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground"
              title="Points — your estimate of this Resource's load on the line. Written to their Supporting Assignment when the plan is set to Staffed; never worked out from hours."
            >
              <input
                inputMode="numeric"
                value={props.readPoints(rowKey)}
                aria-label={`Points for ${share.resourceName}`}
                onChange={(event) => {
                  const next = event.target.value;
                  if (!/^\d{0,2}$/.test(next)) return;
                  props.writePoints(rowKey, next);
                }}
                className="h-6 w-8 rounded-sm border border-border bg-background px-1 text-right text-xs tabular-nums outline-none focus:border-primary"
              />
              pts
            </label>
          ) : undefined
        }
      />
      <SubtreeFigureCells
        geo={geo}
        hours={fmtNum(total)}
        runRate={
          editable ? (
            <input
              inputMode="decimal"
              value={runRate}
              placeholder="0"
              aria-label={`Hours per month for ${share.resourceName}`}
              onChange={(event) => {
                const next = event.target.value;
                if (!isTypableHours(next)) return;
                props.writeRunRate(rowKey, next);
              }}
              className={RUN_RATE_INPUT}
            />
          ) : (
            <span className="block px-1 text-right text-sm tabular-nums">
              {runRate === "" ? "" : fmtNum(runRate)}
            </span>
          )
        }
      />
      {months.map((month, col) => {
        const value = steadyState ? runRate : props.readCell(rowKey, month);
        const loadAt = load?.[col];
        if (!selectable) {
          return (
            <td
              key={month}
              className="relative border-b border-l border-border bg-muted/20 px-1 text-right text-sm text-muted-foreground tabular-nums"
              style={{ width: W_MONTH }}
              title={loadAt?.tooltip ?? undefined}
            >
              {value === "" ? "" : fmtNum(value)}
              {loadAt !== undefined && <LoadBar tier={loadAt.tier} />}
            </td>
          );
        }
        return (
          <Cell
            key={month}
            lineId={rowKey}
            month={month}
            row={row.rowIndex}
            col={col}
            selected={rectContains(props.rect, row.rowIndex, col)}
            showHandle={props.rect.bottom === row.rowIndex && props.rect.right === col}
            readOnly={false}
            projected={false}
            value={value}
            writeCell={props.writeCell}
            commit={props.commit}
            onKeyDown={props.onCellKeyDown}
            onFocus={props.onCellFocus}
            onMouseDown={props.onCellMouseDown}
            onHandleDown={props.onHandleDown}
            loadTier={loadAt?.tier ?? null}
            loadTooltip={loadAt?.tooltip ?? null}
          />
        );
      })}
    </tr>
  );
}

/** An unsplit line's one Resource — its hours ARE the line's, so they mirror, read-only. */
function SoleResourceRow(props: LineSubtreeProps) {
  const { geo, line, months, steadyState } = props;
  void props.rowVersion[line.id];
  const runRate = props.readRunRate(line.id);
  const load = props.rowLoadByKey[line.id];
  return (
    <tr>
      <SubtreeLead geo={geo} name={line.suggestedResourceName ?? "Unnamed Resource"} />
      <SubtreeFigureCells
        geo={geo}
        hours="All"
        runRate={
          <span className="block px-1 text-right text-sm text-muted-foreground tabular-nums">
            {runRate === "" ? "" : fmtNum(runRate)}
          </span>
        }
      />
      {months.map((month, col) => {
        const value = steadyState ? runRate : props.readCell(line.id, month);
        const loadAt = load?.[col];
        return (
          <td
            key={month}
            className="relative border-b border-l border-border bg-muted/20 px-1 text-right text-sm text-muted-foreground tabular-nums"
            style={{ width: W_MONTH }}
            title={loadAt?.tooltip ?? "The line's hours — one Resource covers all of them"}
          >
            {value === "" ? "" : fmtNum(value)}
            {loadAt !== undefined && <LoadBar tier={loadAt.tier} />}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * The split's balance, live from the draft: per month, the line's hours less every Share's.
 * Amber where hours are left for nobody, red where the Shares exceed the line (RP-24.1).
 * Saving refuses an over-allocated month; setting the plan Staffed refuses either.
 */
function UnallocatedRow(props: LineSubtreeProps) {
  const { geo, line, months, steadyState } = props;
  const keys = (line.shares ?? []).map((share) => shareRowKey(line.id, share.resourceId));
  for (const key of [line.id, ...keys]) void props.rowVersion[key];
  const centi = (rowKey: string, month: string): number => {
    const raw = steadyState ? props.readRunRate(rowKey) : props.readCell(rowKey, month);
    return raw === "" ? 0 : toMinor(raw);
  };
  const byMonth = months.map(
    (month) => centi(line.id, month) - keys.reduce((sum, key) => sum + centi(key, month), 0),
  );
  // SteadyState with the projection hidden still has a run-rate to balance.
  const runRateGap = steadyState
    ? centi(line.id, "") - keys.reduce((sum, key) => sum + centi(key, ""), 0)
    : 0;
  const totalGap = steadyState
    ? runRateGap * Math.max(1, months.length)
    : byMonth.reduce((a, b) => a + b, 0);
  const anyOver = steadyState ? runRateGap < 0 : byMonth.some((v) => v < 0);
  const anyUnder = steadyState ? runRateGap > 0 : byMonth.some((v) => v > 0);
  if (!anyOver && !anyUnder) return null;
  const tone = (v: number) =>
    v < 0
      ? "text-destructive font-semibold"
      : v > 0
        ? "text-[var(--fit-moderate)] font-semibold"
        : "text-muted-foreground/50";
  const label = anyOver ? "Over-allocated" : "Unallocated";
  return (
    <tr>
      <td
        className="sticky left-0 z-10 border-b border-border bg-card pl-9 pr-2"
        style={{ width: geo.category.width, height: 28 }}
      >
        <span
          className={cn("text-xs", anyOver ? "text-destructive" : "text-[var(--fit-moderate)]")}
        >
          {anyOver && anyUnder ? "Unallocated / over" : label}
        </span>
      </td>
      <td
        className="sticky z-10 border-b border-border bg-card px-2 text-xs text-muted-foreground"
        style={{ left: geo.who.left, width: geo.who.width }}
      >
        {anyOver ? "More hours than the line has" : "Hours nobody covers yet"}
      </td>
      <SubtreeFigureCells
        geo={geo}
        tone={tone(totalGap)}
        hours={fmtNum(fromCenti(Math.abs(totalGap)))}
        runRate={
          <span className={cn("block px-1 text-right text-sm tabular-nums", tone(runRateGap))}>
            {runRateGap === 0 ? "" : fmtNum(fromCenti(Math.abs(runRateGap)))}
          </span>
        }
      />
      {months.map((month, col) => {
        const v = steadyState ? runRateGap : (byMonth[col] ?? 0);
        return (
          <td
            key={month}
            className={cn(
              "border-b border-l border-border px-1 text-right text-sm tabular-nums",
              tone(v),
            )}
            style={{ width: W_MONTH }}
            title={
              v < 0
                ? `${monthLabel(month)}: ${fmtNum(fromCenti(-v))} hrs more than the line has`
                : v > 0
                  ? `${monthLabel(month)}: ${fmtNum(fromCenti(v))} hrs unallocated`
                  : undefined
            }
          >
            {v === 0 ? "" : `${v < 0 ? "+" : ""}${fmtNum(fromCenti(Math.abs(v)))}`}
          </td>
        );
      })}
    </tr>
  );
}

// ── One cell ──────────────────────────────────────────────────────────────────

interface CellProps {
  lineId: string;
  month: string;
  row: number;
  col: number;
  selected: boolean;
  showHandle: boolean;
  readOnly: boolean;
  /** A steady-state projection rather than an input — rendered muted and read-only. */
  projected: boolean;
  value: string;
  writeCell: (lineId: string, month: string, value: string) => CellPatch;
  commit: (touched: Iterable<string>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, ref: CellRef) => void;
  onFocus: (ref: CellRef) => void;
  onMouseDown: (ref: CellRef, shift: boolean) => void;
  onHandleDown: (event: React.PointerEvent) => void;
  /**
   * The month load bar. `null` = no suggested Resource on this row, not
   * viewable, or the guidance-load overlay is still loading — no bar at all.
   */
  loadTier: GuidanceTier | null;
  /** The hover maths, shown as the `<td>`'s own `title` — never on the input. */
  loadTooltip: string | null;
}

/** Tokens only, per `.claude/rules/design-system.md` — never a literal color. */
function loadBarClass(tier: GuidanceTier): string {
  switch (tier) {
    case "low":
      return "bg-[var(--fit-strong)]";
    case "moderate":
      return "bg-[var(--fit-moderate)]";
    case "high":
      return "bg-[var(--fit-at-risk)]";
    case "none":
      return "bg-muted-foreground/30";
  }
}

/** A thin bar along the bottom edge of the `<td>` — never the input, never the handle. */
function LoadBar({ tier }: { tier: GuidanceTier }) {
  return (
    <span
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-1 bottom-0.5 h-[3px] rounded-full",
        loadBarClass(tier),
      )}
    />
  );
}

function Cell(props: CellProps) {
  const closed = useContext(LockedMonthsContext).has(props.month);
  if (props.projected) {
    // No input at all: a projection is not a field, and rendering it as one invites
    // someone to type into a number that would be overwritten on the next save.
    return (
      <td
        className="relative border-b border-l border-border bg-muted/20 px-1 text-right text-sm text-muted-foreground tabular-nums"
        style={{ width: W_MONTH }}
        title={props.loadTooltip ?? "Projected from the run-rate. Edit the Hrs / mo column."}
      >
        {props.value === "" ? "" : fmtNum(props.value)}
        {props.loadTier !== null && <LoadBar tier={props.loadTier} />}
      </td>
    );
  }

  return (
    <td
      className={cn(
        "relative border-b border-l border-border p-0.5",
        closed && "bg-muted/40",
        props.selected && "bg-primary/10",
      )}
      style={{ width: W_MONTH }}
      title={closed ? CLOSED_MONTH_TITLE : (props.loadTooltip ?? undefined)}
    >
      <input
        data-cell={`${props.row}-${props.col}`}
        // `inputMode` rather than type="number": hours cross the wire as a STRING, and a
        // number input would hand back a float64 and lose the exactness the plan's
        // arithmetic depends on.
        inputMode="decimal"
        value={props.value}
        readOnly={props.readOnly || closed}
        aria-label={`Hours, ${props.month}${closed ? " (closed in NOS)" : ""}`}
        onChange={(event) => {
          if (props.readOnly || closed) return;
          const next = event.target.value;
          // Gate at the keystroke, the same shape RateCardManager uses — a rejected
          // character simply does not appear rather than producing an error.
          if (!isTypableHours(next)) return;
          props.writeCell(props.lineId, props.month, next);
          props.commit([props.lineId]);
        }}
        onFocus={() => props.onFocus({ row: props.row, col: props.col })}
        onMouseDown={(event) =>
          props.onMouseDown({ row: props.row, col: props.col }, event.shiftKey)
        }
        onKeyDown={(event) => props.onKeyDown(event, { row: props.row, col: props.col })}
        className={cn(
          "h-[26px] w-full rounded-sm border border-transparent bg-transparent px-1 text-right text-sm tabular-nums outline-none",
          "focus:border-primary focus:bg-background",
          (props.readOnly || closed) && "cursor-default",
          closed && "text-muted-foreground",
        )}
      />
      {/* Rendered BEFORE the handle so the handle's own square layers on top of it where
          the two would otherwise overlap at the bottom-right corner. */}
      {props.loadTier !== null && <LoadBar tier={props.loadTier} />}
      {props.showHandle && (
        <span
          onPointerDown={props.onHandleDown}
          title="Drag to fill across or down"
          aria-hidden
          className="absolute bottom-0.5 right-0.5 h-[7px] w-[7px] cursor-crosshair rounded-[2px] border-[1.5px] border-background bg-primary"
        />
      )}
    </td>
  );
}
